// @ts-check
// invodiff-data.jsx — sample invoices, vendor presets, comparison engine.
import React from 'react';
import { firebase, fbAuth, fbDb } from './firebase.js';
// Circular with invoice-parser.js (it uses normalizeForMatch/isAutoSku from
// here) and with variance-engine/match-audit (they import the engine from
// here). All cross-references are function calls at runtime, never during
// module init, so the cycles are ESM-legal.
import { lineTypeOf, ambiguousSkuSet } from './invoice-parser.js';
import { classifyVariance, DEFAULT_TOLERANCES } from './variance-engine.jsx';
import { describeMatch } from './match-audit.jsx';

// ── helpers ──────────────────────────────────────────────────────────────
const fmtUSD = (n) => {
  const sign = n < 0 ? '-' : '';
  return sign + '$' + Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};
const fmtDelta = (n) => (n >= 0 ? '+' : '') + fmtUSD(n).replace('+-', '-');

// VULN (audit #7): job.procoreUrl / invoice._fileUrl are rendered as <a href>.
// They come from Firestore, which any provisioned member can write, and React
// does NOT block `javascript:` (or `data:`) URLs in href — only warns. A planted
// `javascript:…` link would run script in a victim's session on click. safeUrl
// allows only http(s) and relative/anchor URLs; anything else collapses to '#'.
const safeUrl = (u) => {
  if (typeof u !== 'string' || u.trim() === '') return undefined;
  const s = u.trim();
  if (/^(https?:|\/|#|\.\/|\.\.\/)/i.test(s) && !/^\s*javascript:/i.test(s)) return s;
  return '#';
};
if (typeof window !== 'undefined') window.safeUrl = safeUrl;

// Produce a Firestore-safe plain copy of `value` before persisting:
//   - converts `undefined` -> `null` (Firestore rejects `undefined`), and
//   - drops any key listed in `dropKeys` (session-only fields like the raw OCR
//     text or the in-memory File handle that must never be written).
// This replaces the old `JSON.parse(JSON.stringify(value, replacer))` idiom.
// structuredClone is NOT a substitute here: it has no replacer, so it would
// keep `undefined` (rejected on write) and faithfully CLONE a File/Blob in
// `_localFile` instead of dropping it. The JSON round-trip was only ever used
// for its side effects (sanitising), so an explicit walk states the intent and
// skips the double serialization. Dates are passed through (Firestore stores
// them as Timestamps); plain data is what these invoice/quote objects hold.
/**
 * @param {any} value
 * @param {Iterable<string>} [dropKeys] keys to omit at every depth
 * @returns {any}
 */
function firestoreSafe(value, dropKeys = []) {
  // Normalize the drop list to a Set once for O(1) per-key lookups (recursive
  // calls pass the Set straight through, so it's built only at the top level).
  const drop = dropKeys instanceof Set ? dropKeys : new Set(dropKeys);
  if (value === undefined) return null;
  if (value === null || typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) return value.map((v) => firestoreSafe(v, drop));
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (drop.has(k)) continue;
    out[k] = firestoreSafe(v, drop);
  }
  return out;
}
if (typeof window !== 'undefined') window.firestoreSafe = firestoreSafe;

// ── Workflow utilities (export / date-range / activity) ────────────────────
// Shared, dependency-free helpers used by the dashboard's export, date-range
// filter, and recent-activity feed. Pure (except downloadFile's DOM side
// effect), so they live here at module scope alongside the other shared data
// helpers and are visible to invodiff-jobs.jsx via the build concat.

// Escape a single CSV cell per RFC 4180: wrap in quotes when it contains a
// comma, quote, or newline, doubling any embedded quote. null/undefined → ''.
function csvCell(v) {
  if (v == null) return '';
  const s = String(v);
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// Build a CSV string from a header row + array-of-arrays data rows. CRLF line
// endings (RFC 4180). Caller prepends a BOM if Excel UTF-8 detection is wanted.
function toCSV(headers, rows) {
  return [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
}

// Trigger a client-side file download. Wrapped in try/catch — Blob/URL APIs can
// throw in locked-down environments; failure logs rather than breaking the UI.
function downloadFile(filename, text, mime = 'text/plain') {
  try {
    const blob = new Blob([text], { type: `${mime};charset=utf-8` });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch (e) {
    console.error('Download failed:', e);
  }
}

// Compact relative time ("just now", "5m ago", "3h ago", "2d ago", then an
// absolute date past a week). Accepts an ISO string, a Firestore Timestamp, or
// a Date. Floors each unit so it never overstates elapsed time.
function relativeTime(input) {
  if (!input) return '';
  let d;
  if (typeof input === 'string')      d = new Date(input);
  else if (input && input.toDate)     d = input.toDate();
  else if (input instanceof Date)     d = input;
  else return '';
  if (isNaN(d.getTime())) return '';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 45)  return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60)  return `${Math.max(1, min)}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)   return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7)   return `${day}d ago`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
}

// The date an invoice "happened" for windowing: the printed document date when
// present (parsed as local midnight so it doesn't drift a day across timezones),
// else the upload timestamp. Returns a Date or null when neither is available.
function invoiceDateOf(inv) {
  if (inv && inv.date) {
    const d = new Date(`${inv.date}T00:00:00`);
    if (!isNaN(d.getTime())) return d;
  }
  if (inv && inv._addedAt) {
    const d = new Date(inv._addedAt);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

// True when an invoice falls inside { from, to } (either bound may be null for
// open-ended). An empty/unset range matches everything; an undated invoice is
// EXCLUDED from any bounded window (it can't be placed on the timeline).
function invoiceInRange(inv, range) {
  if (!range || (!range.from && !range.to)) return true;
  const d = invoiceDateOf(inv);
  if (!d) return false;
  if (range.from && d < range.from) return false;
  if (range.to   && d > range.to)   return false;
  return true;
}

// Standard date-range presets, computed relative to `now` (so "This month" etc.
// stay correct). Each is { id, label, from, to } with from/to as Date bounds
// (to = end of today). 'all' carries null bounds. The dashboard adds a 'custom'
// option in the UI layer. Built as a function because the bounds are
// time-of-call relative.
function dateRangePresets(now = new Date()) {
  const startOf  = (y, m, day) => new Date(y, m, day, 0, 0, 0, 0);
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const y = now.getFullYear(), m = now.getMonth();
  const daysAgo = (n) => startOf(y, m, now.getDate() - n);
  const q = Math.floor(m / 3);
  return [
    { id: 'all',     label: 'All time',     from: null,                 to: null },
    { id: '30d',     label: 'Last 30 days', from: daysAgo(30),          to: endOfDay },
    { id: '90d',     label: 'Last 90 days', from: daysAgo(90),          to: endOfDay },
    { id: 'month',   label: 'This month',   from: startOf(y, m, 1),     to: endOfDay },
    { id: 'quarter', label: 'This quarter', from: startOf(y, q * 3, 1), to: endOfDay },
    { id: 'year',    label: 'This year',    from: startOf(y, 0, 1),     to: endOfDay },
  ];
}

if (typeof window !== 'undefined') {
  window.toCSV            = toCSV;
  window.downloadFile     = downloadFile;
  window.relativeTime     = relativeTime;
  window.invoiceDateOf    = invoiceDateOf;
  window.invoiceInRange   = invoiceInRange;
  window.dateRangePresets = dateRangePresets;
}

// Feature flag: the multi-step invoice approval workflow (NotificationBell, the
// "Pending approvals" dashboard card, the per-invoice "Submit for approval"
// button, and the Approvals queue view) is hidden for now — not relevant at
// this time. The source is intact, but while the flag is off the bundle is NOT
// shipped: under Vite, NOT importing the module is the entire exclusion
// mechanism. To re-enable, do both:
//   1. flip this flag to true;
//   2. add `import './approval-workflow.jsx';` to src/invodiff-app.jsx — it
//      self-registers its components on window, which is how the flag-gated
//      references there resolve.
// (Pre-M3 this also needed a jsx-compile skip-list entry and a dist-src
// <script> tag in index.html; both disappeared with the Vite/ESM migration.)
if (typeof window !== 'undefined') window.APPROVALS_ENABLED = false;

// ── Role system (app-wide) ──────────────────────────────────────────────────
// The provisioning vocabulary used by the TopBar role pill, the Admin panel,
// and (when enabled) the approval workflow. It lives HERE — not in
// approval-workflow.jsx — because roles are active even while the approvals
// feature is flagged off and its bundle is excluded from the page.
const ROLES = ['ESTIMATOR', 'PROJECT_MANAGER', 'ACCOUNTING', 'EXECUTIVE', 'ADMIN'];

const ROLE_LABEL = {
  ESTIMATOR:       'Estimator',
  PROJECT_MANAGER: 'Project Manager',
  ACCOUNTING:      'Accounting',
  EXECUTIVE:       'Executive',
  ADMIN:           'Admin',
};

const ROLE_COLOR = {
  ESTIMATOR:       'var(--accent)',
  PROJECT_MANAGER: 'var(--ok)',
  ACCOUNTING:      'var(--info)',
  EXECUTIVE:       'var(--warn)',
  ADMIN:           'var(--bad)',
};

if (typeof window !== 'undefined') Object.assign(window, { ROLES, ROLE_LABEL, ROLE_COLOR });

// ═══ Invoice storage layer (M2) ═════════════════════════════════════════════
// Where a job's invoices live is transitioning between two shapes:
//
//   legacy — the embedded `invoices` array on the job document. Two structural
//            problems: the whole job (quote + every parsed invoice) must fit
//            Firestore's 1 MiB document cap, and every edit is a whole-array
//            read-modify-write, so two teammates editing the same job silently
//            lose one side's change.
//   split  — one document per invoice in jobs/{id}/invoices/{autoId}, ordered
//            by a `_seq` field. Selected by the `invoicesInSub` marker on the
//            job doc. Per-invoice docs remove the ceiling and make every edit
//            a single-doc write (no lost updates).
//
// Everything below is dual-shape: reads assemble `invoices` from whichever
// shape the job uses, writes route to the right place, and the migration tool
// (window.__migrateJobInvoices) moves a job from legacy to split only after
// verifying the budget aggregation is byte-identical on both shapes. Rollback
// is clearing the marker — the legacy array is left frozen in place until a
// later cleanup pass, well after the split shape has soaked.
//
// NOTE: the shared store's collection-group listener needs the read rule
//   match /{path=**}/invoices/{invoiceId} { allow read: if isMember(); }
// from firestore.rules deployed. Until it is, the store degrades gracefully
// (legacy arrays keep working; migrated jobs would show no invoices in the
// global views) and __migrateJobInvoices refuses to run.

const usesSubInvoices = (jobData) => !!(jobData && jobData.invoicesInSub);

const invoicesColOf = (jobId) => fbDb.collection('jobs').doc(jobId).collection('invoices');

// Subcollection docs → the array shape every consumer already expects.
// `_docId` (session-only, never persisted) lets write ops target the doc.
function assembleSubInvoices(snap) {
  return snap.docs
    .map((d) => ({ ...d.data(), _docId: d.id }))
    .sort((a, b) => (a._seq || 0) - (b._seq || 0));
}

// ── Firestore doc-size watch ────────────────────────────────────────────────
// Firestore hard-caps a document at 1 MiB; a legacy-shape job embeds its whole
// quote + every invoice, so it grows with every upload until writes fail
// outright. Warn (console + admin-visible error log) well before the cliff.
// Split-shape jobs are immune — their job doc holds only metadata + quote.
const JOB_DOC_WARN_BYTES = 700 * 1024;
const _jobSizeWarned = new Set();
function warnIfJobDocLarge(job) {
  if (usesSubInvoices(job)) return;
  let bytes;
  try { bytes = new Blob([JSON.stringify(job)]).size; } catch (_) { return; }
  if (bytes < JOB_DOC_WARN_BYTES || _jobSizeWarned.has(job.id)) return;
  _jobSizeWarned.add(job.id);
  const kb = Math.round(bytes / 1024);
  const label = job.jobNumber || job.id;
  console.warn(`[job-size] Job ${label} is ~${kb} KB of Firestore's 1024 KB document cap — writes fail at the cap. Migrate it to the invoices subcollection (window.__migrateJobInvoices).`);
  if (typeof window.__logError === 'function') {
    window.__logError({ scope: 'job-size', message: `job ${label} at ~${kb} KB of the 1024 KB Firestore cap` });
  }
}

// ── Shared jobs store ───────────────────────────────────────────────────────
// ONE Firestore subscription for the whole session, shared by every global
// view (dashboard, jobs list, price history) — previously each view held its
// own full-collection listener, doubling reads and allowing the views to
// disagree mid-flight. Joins the jobs collection with a collection-group
// listener over invoices subcollections so split-shape jobs present the same
// `job.invoices` array shape as legacy ones; consumers cannot tell the
// difference. Subscriptions are torn down and rebuilt on auth change.
const _jobsStore = {
  subs: new Set(),          // setState callbacks of mounted useJobsStore hooks
  uid: null,                // auth uid the current subscriptions belong to
  unsubJobs: null,
  unsubCg: null,
  authWatcher: false,
  rawJobs: null,            // null = still loading
  rawById: null,            // Map(jobId → raw job obj) — identity reuse across snaps
  cgByJob: null,            // Map(jobId → invoices[]) | null before first snap
  cgFailed: false,          // collection-group rule not deployed / denied
  permError: false,
  mergedById: null,         // Map(jobId → {src, invoices, out}) — merged-obj reuse
  value: { jobs: null, loading: true, permError: false },
};

// Shared empty-invoices constant: a split job with no invoices yet must present
// the SAME array identity every recompute, or the merged-object cache below
// (and every per-job memo downstream) misses forever.
const _NO_INVOICES = Object.freeze([]);

function _jobsStoreRecompute() {
  const s = _jobsStore;
  let jobs = null;
  if (s.rawJobs) {
    // Identity contract: a job whose raw doc AND invoices list are unchanged
    // keeps its exact object reference from the previous emission. Downstream
    // per-job memos (dashboard stats, price history) rely on this to skip
    // recomputing untouched jobs when any single doc changes.
    const prevMerged = s.mergedById || new Map();
    const merged = new Map();
    jobs = s.rawJobs.map((j) => {
      if (!usesSubInvoices(j)) return j;
      const invoices = (s.cgByJob && s.cgByJob.get(j.id)) || _NO_INVOICES;
      const prev = prevMerged.get(j.id);
      const out = (prev && prev.src === j && prev.invoices === invoices)
        ? prev.out
        : { ...j, invoices };
      merged.set(j.id, { src: j, invoices, out });
      return out;
    });
    s.mergedById = merged;
  }
  s.value = { jobs, loading: jobs === null && !s.permError, permError: s.permError };
  for (const cb of s.subs) cb(s.value);
}

function _jobsStoreTeardown() {
  const s = _jobsStore;
  if (s.unsubJobs) { s.unsubJobs(); s.unsubJobs = null; }
  if (s.unsubCg)   { s.unsubCg();   s.unsubCg = null; }
  s.rawJobs = null; s.rawById = null; s.cgByJob = null; s.cgFailed = false;
  s.permError = false; s.mergedById = null;
  s.value = { jobs: null, loading: true, permError: false };
}

function _jobsStoreEnsure() {
  const s = _jobsStore;
  if (!s.authWatcher) {
    s.authWatcher = true;
    fbAuth.onAuthStateChanged((u) => {
      const uid = u ? u.uid : null;
      if (uid === s.uid) return;
      _jobsStoreTeardown();
      s.uid = uid;
      if (uid && s.subs.size) _jobsStoreEnsure();
      else for (const cb of s.subs) cb(s.value);
    });
  }
  const u = fbAuth.currentUser;
  if (!u || s.unsubJobs) return;
  s.uid = u.uid;
  s.unsubJobs = fbDb.collection('jobs').orderBy('createdAt', 'desc').onSnapshot(
    (snap) => {
      // Rebuild the array (order can change) but only materialize a fresh
      // object for docs Firestore reports as added/modified — unchanged jobs
      // keep their previous identity. Doc-size check runs only on fresh docs
      // (it JSON-stringifies legacy jobs, too costly for every snapshot).
      const changed = new Set(snap.docChanges().map((c) => c.doc.id));
      const prev = s.rawById || new Map();
      const byId = new Map();
      s.rawJobs = snap.docs.map((d) => {
        let j = changed.has(d.id) ? null : prev.get(d.id);
        if (!j) { j = { id: d.id, ...d.data() }; warnIfJobDocLarge(j); }
        byId.set(d.id, j);
        return j;
      });
      s.rawById = byId;
      s.permError = false;
      _jobsStoreRecompute();
    },
    (err) => {
      if (err && err.code === 'permission-denied') s.permError = true;
      s.rawJobs = s.rawJobs || [];
      _jobsStoreRecompute();
    }
  );
  s.unsubCg = fbDb.collectionGroup('invoices').onSnapshot(
    (snap) => {
      // Same identity contract as the jobs listener: only jobs with a doc
      // change in THIS snapshot get their invoices array rebuilt; every other
      // job keeps its previous array reference.
      const prev = s.cgByJob;
      let affected = null;                          // null = first snap, build all
      if (prev) {
        affected = new Set();
        for (const c of snap.docChanges()) {
          const parent = c.doc.ref.parent.parent;   // jobs/{jobId}
          if (parent) affected.add(parent.id);
        }
      }
      const byJob = new Map();
      const rebuilt = new Set();
      for (const d of snap.docs) {
        const parent = d.ref.parent.parent;
        if (!parent) continue;
        const jid = parent.id;
        if (affected && !affected.has(jid)) {
          if (!byJob.has(jid)) byJob.set(jid, prev.get(jid) || _NO_INVOICES);
          continue;
        }
        let list = byJob.get(jid);
        if (!list) { list = []; byJob.set(jid, list); rebuilt.add(jid); }
        list.push({ ...d.data(), _docId: d.id });
      }
      for (const jid of rebuilt) byJob.get(jid).sort((a, b) => (a._seq || 0) - (b._seq || 0));
      s.cgByJob = byJob;
      s.cgFailed = false;
      _jobsStoreRecompute();
    },
    (err) => {
      // Most likely the collection-group read rule isn't deployed yet. Legacy
      // jobs are unaffected; migrated jobs would show empty in global views,
      // which is why __migrateJobInvoices probes this rule before running.
      if (!s.cgFailed) console.warn('[jobs-store] collection-group invoices listener failed (deploy the firestore.rules update):', err && err.code);
      s.cgFailed = true;
      s.cgByJob = s.cgByJob || new Map();
      _jobsStoreRecompute();
    }
  );
}

// { jobs: array|null, loading, permError } — jobs is null until the first
// snapshot. The subscription outlives any single view (session lifetime), so
// switching Dashboard ↔ Jobs ↔ Price History doesn't resubscribe or reload.
function useJobsStore() {
  const [value, setValue] = React.useState(_jobsStore.value);
  React.useEffect(() => {
    _jobsStore.subs.add(setValue);
    _jobsStoreEnsure();
    setValue(_jobsStore.value);
    return () => { _jobsStore.subs.delete(setValue); };
  }, []);
  return value;
}

// ── Per-job live data ───────────────────────────────────────────────────────
// Job doc + its invoices, dual-shape, live. Every in-job view (Summary,
// Materials, Invoices, Issues, match audit) previously hand-rolled this
// subscription; this is the single implementation. Pass null (e.g. the sample
// job) to subscribe to nothing.
function useJobData(jobId) {
  const [state, setState] = React.useState({ exists: false, data: null, invoices: [], synced: false });
  React.useEffect(() => {
    if (!jobId) {
      setState({ exists: false, data: null, invoices: [], synced: false });
      return undefined;
    }
    setState((prev) => ({ ...prev, synced: false }));
    let jobDoc = null, jobSeen = false, subInvoices = null, subSeen = false;
    const push = () => {
      if (!jobSeen || !subSeen) return;
      const d = jobDoc && jobDoc.exists ? jobDoc.data() : null;
      const invoices = !d ? []
        : usesSubInvoices(d) ? (subInvoices || [])
        : (d.invoices ?? []);
      setState({ exists: !!d, data: d, invoices, synced: true });
    };
    const ref = fbDb.collection('jobs').doc(jobId);
    const unsubJob = ref.onSnapshot(
      (doc) => { jobDoc = doc; jobSeen = true; push(); },
      (err) => { console.error('[job-data] job listener failed:', err); jobDoc = null; jobSeen = true; push(); }
    );
    const unsubInv = ref.collection('invoices').onSnapshot(
      (snap) => { subInvoices = assembleSubInvoices(snap); subSeen = true; push(); },
      (err)  => { console.warn('[job-data] invoices listener failed:', err && err.code); subInvoices = []; subSeen = true; push(); }
    );
    return () => { unsubJob(); unsubInv(); };
  }, [jobId]);
  return state;
}

// ── Shape-aware invoice writes ──────────────────────────────────────────────
// `useSub` is the job's current shape (usesSubInvoices of its live doc data).
// Split shape: single-doc operations — concurrent-safe. Legacy shape: the
// existing array operations, byte-for-byte what the app always did.
const invoiceStore = {
  add(jobId, useSub, entry) {
    if (useSub) return invoicesColOf(jobId).add({ ...entry, _seq: Date.now() });
    return fbDb.collection('jobs').doc(jobId)
      .update({ invoices: firebase.firestore.FieldValue.arrayUnion(entry) });
  },
  removeAt(jobId, useSub, invoices, idx) {
    if (useSub) {
      const target = invoices[idx];
      if (!target || !target._docId) return Promise.reject(new Error('Invoice record is not addressable yet — retry in a moment.'));
      return invoicesColOf(jobId).doc(target._docId).delete();
    }
    return fbDb.collection('jobs').doc(jobId)
      .update({ invoices: invoices.filter((_, i) => i !== idx) });
  },
  // Replace the invoice at idx with `next` (used by hide-toggle and re-parse).
  writeAt(jobId, useSub, invoices, idx, next) {
    if (useSub) {
      const target = invoices[idx];
      if (!target || !target._docId) return Promise.reject(new Error('Invoice record is not addressable yet — retry in a moment.'));
      const persist = { ...next };
      delete persist._docId;                       // session-only address
      persist._seq = target._seq ?? Date.now();    // keep its position
      return invoicesColOf(jobId).doc(target._docId).set(persist);
    }
    return fbDb.collection('jobs').doc(jobId)
      .update({ invoices: invoices.map((x, i) => (i === idx ? next : x)) });
  },
};

// Delete a job AND its invoices subcollection. Deleting a Firestore document
// never deletes its subcollections — a bare doc.delete() on a split-shape job
// would orphan every invoice doc (invisible in the UI, still billed/stored).
async function deleteJobDeep(jobId) {
  const ref = fbDb.collection('jobs').doc(jobId);
  try {
    const snap = await ref.collection('invoices').get();
    for (let i = 0; i < snap.docs.length; i += 400) {
      const batch = fbDb.batch();
      snap.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
  } catch (e) {
    console.warn('[delete-job] could not purge invoices subcollection first:', e && e.code);
  }
  await ref.delete();
}

// ── Migration: legacy array → invoices subcollection ────────────────────────
// Run from the browser console by an admin/member:
//   __migrateJobInvoices()                dry run — reports, writes NOTHING
//   __migrateJobInvoices({ apply: true }) migrate, verify-gated per job
//
// Per job: write the array's invoices as subcollection docs, read them back,
// run the budget aggregation (aggregateInvoices per trade segment) on BOTH
// shapes, and only if the outputs are byte-identical set `invoicesInSub`.
// On any mismatch the written docs are deleted and the job stays legacy.
// The legacy array is never modified — rollback = clear the marker.
function _aggregateForVerify(jobData, invoices) {
  const visible = (invoices || []).filter((inv) => !inv._hidden);
  const segs = getSegments({ ...jobData, invoices: visible });
  return segs.map((seg) => (seg.quote
    ? aggregateInvoices(seg.quote, seg.invoices, [], { taxExempt: !!jobData.taxExempt })
    : null));
}

async function __migrateJobInvoices(opts = {}) {
  const apply = !!opts.apply;
  try {
    await fbDb.collectionGroup('invoices').limit(1).get();
  } catch (e) {
    const msg = 'Aborted: the collection-group read rule for invoices is not deployed. Publish the updated firestore.rules first.';
    console.error(msg, e && e.code);
    return [{ status: 'aborted', note: msg }];
  }

  const snap = await fbDb.collection('jobs').get();
  const report = [];
  for (const doc of snap.docs) {
    const d = doc.data();
    const label = d.jobNumber || doc.id;
    if (usesSubInvoices(d)) { report.push({ job: label, status: 'already-split' }); continue; }
    const arr = Array.isArray(d.invoices) ? d.invoices : [];

    if (!arr.length) {
      if (apply) await doc.ref.update({ invoicesInSub: true });
      report.push({ job: label, status: apply ? 'marked (no invoices)' : 'would mark (no invoices)' });
      continue;
    }

    const before = JSON.stringify(_aggregateForVerify(d, arr));
    if (!apply) {
      const after = JSON.stringify(_aggregateForVerify(d, arr.map((inv, i) => ({ ...inv, _seq: i + 1 }))));
      report.push({
        job: label, invoices: arr.length,
        status: before === after ? 'ready' : 'DRY-RUN MISMATCH — investigate before applying',
      });
      continue;
    }

    // Write, read back, verify, then mark.
    const col = doc.ref.collection('invoices');
    const written = [];
    try {
      for (let i = 0; i < arr.length; i += 400) {
        const batch = fbDb.batch();
        arr.slice(i, i + 400).forEach((inv, j) => {
          const ref = col.doc();
          written.push(ref);
          batch.set(ref, { ...inv, _seq: i + j + 1 });
        });
        await batch.commit();
      }
      const back = await col.get();
      const rebuilt = assembleSubInvoices(back);
      const after = JSON.stringify(_aggregateForVerify(d, rebuilt));
      if (before !== after) throw new Error('post-write aggregation mismatch');
      await doc.ref.update({ invoicesInSub: true });
      report.push({ job: label, invoices: arr.length, status: 'migrated' });
    } catch (e) {
      for (let i = 0; i < written.length; i += 400) {
        const batch = fbDb.batch();
        written.slice(i, i + 400).forEach((r) => batch.delete(r));
        await batch.commit().catch(() => {});
      }
      report.push({ job: label, invoices: arr.length, status: `FAILED — rolled back (${e.message})` });
    }
  }
  if (typeof console.table === 'function') console.table(report);
  return report;
}

// ── Shared vendor rules (M2.3) ──────────────────────────────────────────────
// Vendor rules (ignores, tolerances, learned aliases, notes) were stored
// per-user at users/{uid}.vendorRules — so each estimator trained their own
// matcher, and the same job gave different answers to different teammates.
// The shared shape is one doc per vendor in /vendorRules/{encodedVendor}
// ({ vendor, rules, updatedAt, updatedBy }), gated by a `_meta` marker doc:
//
//   marker absent  → legacy mode: per-user map, exactly the old behavior.
//   marker present → shared mode: everyone reads/writes the team collection.
//
// Saves in shared mode are per-vendor diffs (only vendors you changed are
// written, removed vendors deleted), so two teammates editing DIFFERENT
// vendors can't clobber each other. Migration is the admin console tool
// window.__migrateVendorRules (dry run by default); rollback is deleting the
// `_meta` doc — the per-user maps are left frozen in place.
const VENDOR_RULES_META_ID = '_meta';
// Doc IDs can't contain '/' or be empty; the stored `vendor` field is
// authoritative, the ID is just an address ('' → '_').
const vendorRuleDocId = (vendor) => (vendor ? encodeURIComponent(vendor) : '_');

function useVendorRules(user, seedHistory) {
  const uid = user ? user.uid : null;
  const [vendorRules, setVendorRulesState] = React.useState({});
  const [rulesReady, setRulesReady] = React.useState(false);
  const [shared, setShared] = React.useState(false);
  const modeRef   = React.useRef(null);   // 'shared' | 'legacy' | null
  const snapRef   = React.useRef('{}');   // JSON of the last persisted value
  const timerRef  = React.useRef(null);
  const seededRef = React.useRef(false);
  const currentRef = React.useRef({});    // live mirror for the snapshot handler
  // Written in an effect, not during render — render must stay pure since
  // React can discard/replay it. Only read from the async snapshot handler
  // below, which always fires after this effect has committed.
  React.useEffect(() => { currentRef.current = vendorRules; }, [vendorRules]);

  React.useEffect(() => {
    modeRef.current = null;
    seededRef.current = false;
    snapRef.current = '{}';
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setVendorRulesState({});
    setRulesReady(false);
    setShared(false);
    if (!uid) return undefined;
    let cancelled = false;

    // Legacy fallback — the original per-user load, verbatim semantics
    // (including the dev seed on first use).
    const startLegacy = () => {
      if (cancelled || modeRef.current === 'legacy') return;
      modeRef.current = 'legacy';
      setShared(false);
      const init = (saved) => {
        let initial;
        if (saved) initial = saved;
        else if (seedHistory) { initial = { ...SEED_VENDOR_RULES }; seededRef.current = true; }
        else initial = {};
        snapRef.current = JSON.stringify(initial);
        setVendorRulesState(initial);
        setRulesReady(true);
      };
      fbDb.collection('users').doc(uid).get()
        .then((doc) => { if (!cancelled && modeRef.current === 'legacy') init(doc.exists ? (doc.data().vendorRules || null) : null); })
        .catch(()   => { if (!cancelled && modeRef.current === 'legacy') init(null); });
    };

    const unsub = fbDb.collection('vendorRules').onSnapshot(
      (snap) => {
        if (cancelled) return;
        if (!snap.docs.some((d) => d.id === VENDOR_RULES_META_ID)) { startLegacy(); return; }
        const wasShared = modeRef.current === 'shared';
        modeRef.current = 'shared';
        setShared(true);
        // Local edits not yet saved? Skip this remote echo — the debounced
        // diff-save lands shortly and the next snapshot converges both sides.
        if (wasShared && JSON.stringify(currentRef.current) !== snapRef.current) return;
        const map = {};
        for (const d of snap.docs) {
          if (d.id === VENDOR_RULES_META_ID) continue;
          const data = d.data();
          map[data.vendor ?? ''] = data.rules || [];
        }
        snapRef.current = JSON.stringify(map);
        setVendorRulesState(map);
        setRulesReady(true);
      },
      // Listener denied → the vendorRules rule isn't deployed yet; run legacy.
      () => { if (!cancelled) startLegacy(); }
    );

    return () => { cancelled = true; unsub(); clearTimeout(timerRef.current); timerRef.current = null; };
  }, [uid]);

  // Dev seed toggle — legacy mode only. In shared mode it would push sample
  // rules to the whole team, so it's deliberately inert there.
  React.useEffect(() => {
    if (!rulesReady || modeRef.current !== 'legacy') return;
    if (seedHistory && !seededRef.current) {
      setVendorRulesState((prev) => ({ ...SEED_VENDOR_RULES, ...prev }));
      seededRef.current = true;
    } else if (!seedHistory && seededRef.current) {
      setVendorRulesState({});
      seededRef.current = false;
    }
  }, [seedHistory, rulesReady]);

  // Debounced save. Legacy: whole-map field update on the user doc (original
  // behavior — `update` so deletions stick, `set(merge)` fallback for a
  // missing doc). Shared: per-vendor diff against the last persisted value.
  React.useEffect(() => {
    if (!uid || !rulesReady) return;
    const current = JSON.stringify(vendorRules);
    if (current === snapRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const prev = JSON.parse(snapRef.current || '{}');
      snapRef.current = current;
      if (modeRef.current === 'shared') {
        const batch = fbDb.batch();
        let ops = 0;
        for (const [vendor, rules] of Object.entries(vendorRules)) {
          if (JSON.stringify(rules) === JSON.stringify(prev[vendor])) continue;
          batch.set(fbDb.collection('vendorRules').doc(vendorRuleDocId(vendor)), {
            vendor, rules: firestoreSafe(rules),
            updatedAt: new Date().toISOString(), updatedBy: uid,
          });
          ops++;
        }
        for (const vendor of Object.keys(prev)) {
          if (!(vendor in vendorRules)) {
            batch.delete(fbDb.collection('vendorRules').doc(vendorRuleDocId(vendor)));
            ops++;
          }
        }
        if (ops) batch.commit().catch(console.error);
      } else {
        const ref = fbDb.collection('users').doc(uid);
        ref.update({ vendorRules })
          .catch(() => ref.set({ vendorRules }, { merge: true }))
          .catch(console.error);
      }
    }, 1000);
    return () => clearTimeout(timerRef.current);
  }, [vendorRules, uid, rulesReady]);

  return { vendorRules, setVendorRules: setVendorRulesState, rulesReady, shared };
}

// Migration: per-user vendorRules maps → shared /vendorRules collection.
// Run from the console AS AN ADMIN (reading all users requires it):
//   __migrateVendorRules()                dry run — reports, writes NOTHING
//   __migrateVendorRules({ apply: true }) merge + write + set the _meta marker
// Merge policy: concat every user's rules per vendor; exact-JSON duplicates
// dropped; an id collision with different content keeps both, re-id'ing the
// later one. Vendor docs are written BEFORE the marker, so no client flips to
// shared mode until the full data set exists.
async function __migrateVendorRules(opts = {}) {
  const apply = !!opts.apply;
  try {
    await fbDb.collection('vendorRules').limit(1).get();
  } catch (e) {
    const msg = 'Aborted: /vendorRules is not readable. Publish the updated firestore.rules first.';
    console.error(msg, e && e.code);
    return [{ status: 'aborted', note: msg }];
  }
  const meta = await fbDb.collection('vendorRules').doc(VENDOR_RULES_META_ID).get();
  if (meta.exists) {
    console.warn('Vendor rules are already shared (the _meta marker exists) — nothing to do.');
    return [{ status: 'already-shared' }];
  }
  let usersSnap;
  try {
    usersSnap = await fbDb.collection('users').get();
  } catch (e) {
    const msg = 'Aborted: could not read the users collection — run this signed in as an ADMIN.';
    console.error(msg, e && e.code);
    return [{ status: 'aborted', note: msg }];
  }

  const merged = {};
  for (const u of usersSnap.docs) {
    const vr = u.data().vendorRules || {};
    for (const [vendor, rules] of Object.entries(vr)) {
      const bucket = (merged[vendor] = merged[vendor] || []);
      for (const rule of rules || []) {
        if (bucket.some((r) => JSON.stringify(r) === JSON.stringify(rule))) continue;
        bucket.push(bucket.some((r) => r.id === rule.id)
          ? { ...rule, id: `${rule.id}-${u.id.slice(0, 4)}` }
          : rule);
      }
    }
  }

  const report = Object.entries(merged).map(([vendor, rules]) => ({
    vendor: vendor || '(no vendor)', rules: rules.length,
    status: apply ? 'migrated' : 'ready',
  }));
  if (apply) {
    const entries = Object.entries(merged);
    for (let i = 0; i < entries.length; i += 400) {
      const batch = fbDb.batch();
      entries.slice(i, i + 400).forEach(([vendor, rules]) => {
        batch.set(fbDb.collection('vendorRules').doc(vendorRuleDocId(vendor)), {
          vendor, rules: firestoreSafe(rules),
          updatedAt: new Date().toISOString(), updatedBy: 'migration',
        });
      });
      await batch.commit();
    }
    await fbDb.collection('vendorRules').doc(VENDOR_RULES_META_ID)
      .set({ migrated: true, at: new Date().toISOString() });
  }
  if (typeof console.table === 'function') console.table(report);
  return report;
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep,
    useVendorRules,
    __migrateJobInvoices, __migrateVendorRules,
  });
}

// UX #5 (a11y): shared focus-trap hook for modals/drawers. On open it moves
// focus into the dialog, keeps Tab/Shift+Tab cycling within it (so keyboard
// users can't tab into the obscured page behind), and restores focus to the
// element that opened it on close. Pass a ref to the dialog container.
function useFocusTrap(ref, active = true) {
  React.useEffect(() => {
    if (!active || !ref.current) return undefined;
    const node = ref.current;
    const opener = /** @type {HTMLElement | null} */ (document.activeElement); // restore here on close
    const SELECTOR =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const focusables = () =>
      Array.from(/** @type {NodeListOf<HTMLElement>} */ (node.querySelectorAll(SELECTOR)))
        .filter((el) => el.offsetParent !== null);

    // Move focus inside the dialog (first focusable, else the container itself).
    const first = focusables()[0];
    if (first) first.focus();
    else { node.setAttribute('tabindex', '-1'); node.focus(); }

    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl  = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault(); lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault(); firstEl.focus();
      }
    };
    node.addEventListener('keydown', onKey);
    return () => {
      node.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function') opener.focus(); // restore focus
    };
  }, [ref, active]);
}
if (typeof window !== 'undefined') window.useFocusTrap = useFocusTrap;

// UOM normalisation — maps any footage synonym to canonical 'LF'
const FOOTAGE_RE = /^(lf|l\.f\.|lin(eal?|ear?)\.?\s*(ft?\.?|foot|feet)|ft\.?|foot|feet|')$/i;
const normalizeUOM = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  return FOOTAGE_RE.test(s) ? 'LF' : s.toUpperCase();
};
const isFootageUOM = (uom) => uom === 'LF';

// ── UOM compatibility & conversion ────────────────────────────────────────
// Quote and invoice often bill the same material on different bases: sticks
// (EA/LN/PC) vs linear feet, per-hundred (C) / per-thousand (M) pricing, or
// box/bundle packs. Where the factor is derivable, invoice lines are converted
// onto the quote line's basis; otherwise the pair is marked incompatible so
// the UI shows a "unit mismatch" instead of bogus qty/price variances.

// Count-style units that all mean "one piece/stick" — interchangeable as-is.
const STICK_UOMS = new Set(['EA', 'LN', 'PC', 'PCS', 'STK', 'LEN']);
// Units priced per N pieces.
const PER_EA = { C: 100, M: 1000 };
// Pack units whose piece count is usually printed in the description. These run
// against raw description text, so they stay loose on spacing and case:
// "1/box", "10 / bdl", "50/PK" and "100 / carton" all match.
const PACK_RE = {
  BX:  /(\d+)\s*\/\s*(?:bx|box)\b/i,
  BDL: /(\d+)\s*(?:ln\s*)?\/\s*(?:bdl|bundle)\b/i,
  PK:  /(\d+)\s*\/\s*(?:pk|pack)\b/i,
  CTN: /(\d+)\s*\/\s*(?:ctn|carton)\b/i,
};

// Stick length in feet from a description, e.g. "1-1/4x20 FT TYPE L ..." → 20.
function stickFeet(desc) {
  const m = /(?:^|x|\s)(\d{1,3}(?:\.\d+)?)\s*(?:ft\b|foot\b|feet\b|')/i.exec(desc || '');
  return m ? parseFloat(m[1]) : null;
}

function packCount(desc, uom) {
  const re = PACK_RE[uom];
  const m = re && re.exec(desc || '');
  return m ? parseInt(m[1], 10) : null;
}

// Linear footage represented by a line — direct for LF, derived from
// stick count × stick length for EA/LN/PC lines like "...x20 FT...".
function itemFootage(it, uomN) {
  const u = uomN !== undefined ? uomN : normalizeUOM(it.uom);
  if (u === 'LF') return it.qty;
  if (STICK_UOMS.has(u)) {
    const ft = stickFeet(it.desc);
    if (ft) return +(it.qty * ft).toFixed(2);
  }
  return null;
}

// Display labels for the UOM codes above, grouped the same way.
const UOM_LABEL = {
  // stick-style counts
  EA: 'each', LN: 'length', LEN: 'length', PC: 'piece', PCS: 'pieces', STK: 'stick',
  // footage
  LF: 'linear foot',
  // priced per N pieces
  C: 'per hundred', M: 'per thousand',
  // packs
  BX: 'box', BDL: 'bundle', PK: 'pack', CTN: 'carton',
};

// Human-readable label for a raw UOM, e.g. "ea" → "each", "ft" → "linear foot".
// Falls through to the normalized code when there's no label for it.
function displayUOM(uom) {
  const n = normalizeUOM(uom);
  return UOM_LABEL[n] || n;
}

// Convert one invoice line onto the quote line's unit basis.
// Returns { qty, unit, converted, compatible }.
function convertToQuoteBasis(quoteItem, ii) {
  const raw = { qty: ii.qty, unit: ii.unit, converted: false, compatible: true };
  if (!quoteItem) return raw;
  const qU = normalizeUOM(quoteItem.uom);
  const iU = ii.uomNorm !== undefined ? ii.uomNorm : normalizeUOM(ii.uom);
  if (!qU || !iU || qU === iU) return raw;
  if (STICK_UOMS.has(qU) && STICK_UOMS.has(iU)) return raw; // EA ≈ LN ≈ PC

  // f = quote-units per ONE invoice-unit; qty×f and unit÷f land on quote basis.
  let f = null;
  if (PER_EA[iU] && STICK_UOMS.has(qU)) f = PER_EA[iU];
  else if (PER_EA[qU] && STICK_UOMS.has(iU)) f = 1 / PER_EA[qU];
  else if (qU === 'LF' && STICK_UOMS.has(iU)) {
    const ft = stickFeet(ii.desc) ?? stickFeet(quoteItem.desc);
    if (ft) f = ft;
  } else if (iU === 'LF' && STICK_UOMS.has(qU)) {
    const ft = stickFeet(quoteItem.desc) ?? stickFeet(ii.desc);
    if (ft) f = 1 / ft;
  } else if (PACK_RE[iU] && STICK_UOMS.has(qU)) {
    f = packCount(ii.desc, iU) ?? packCount(quoteItem.desc, iU);
  } else if (PACK_RE[qU] && STICK_UOMS.has(iU)) {
    const pc = packCount(quoteItem.desc, qU) ?? packCount(ii.desc, qU);
    if (pc) f = 1 / pc;
  }

  if (!f) return { ...raw, compatible: false };
  return {
    qty: +(ii.qty * f).toFixed(4),
    unit: +(ii.unit / f).toFixed(6),
    converted: true,
    compatible: true,
  };
}

// ── sample data ──────────────────────────────────────────────────────────
const SAMPLE_VENDORS = [
  'Acme Industrial Supply',
  'Northbridge Logistics',
  'Pacific Materials Co.',
  'Vertex Fabrication',
  'Helios Components Inc.',
];

const INVOICE_A = {
  id: 'PO-29481',
  label: 'Purchase Order',
  vendor: 'Acme Industrial Supply',
  date: '2026-04-22',
  poRef: 'PO-29481',
  currency: 'USD',
  terms: 'Net 30',
  items: [
    { sku: 'BR-204-S',  desc: 'Steel L-bracket, 4"x4", galvanized',  qty: 100,  uom: 'EA', unit: 12.50 },
    { sku: 'HX-M8-50',  desc: 'Hex bolts, M8 x 50mm, A2 stainless',  qty: 500,  uom: 'EA', unit: 0.45  },
    { sku: 'WS-M8-FL',  desc: 'Flat washers, M8, zinc plated',       qty: 1000, uom: 'EA', unit: 0.12  },
    { sku: 'AL-SH-3MM', desc: 'Aluminum sheet, 3mm, 1m x 2m',        qty: 20,   uom: 'EA', unit: 85.00 },
    { sku: 'GR-SOLD',   desc: 'Solder wire, 1lb spool, lead-free',   qty: 6,    uom: 'EA', unit: 28.00 },
    { sku: 'COND-34',   desc: '3/4" EMT conduit',                    qty: 500,  uom: 'LF', unit: 3.20  },
  ],
  shipping: 45.00,
  taxRate: 0.08,
};

const INVOICE_B = {
  id: 'INV-77214',
  label: 'Vendor Invoice',
  vendor: 'Acme Industrial Supply',
  date: '2026-05-14',
  poRef: 'PO-29481',
  currency: 'USD',
  terms: 'Net 30',
  items: [
    { sku: 'BR-204-S',  desc: 'Steel L-bracket, 4"x4", galvanized',  qty: 100,  uom: 'EA',        unit: 12.75 },  // price up
    { sku: 'HX-M8-50',  desc: 'Hex bolts, M8 x 50mm, A2 stainless',  qty: 500,  uom: 'EA',        unit: 0.45  },  // match
    { sku: 'WS-M8-FL',  desc: 'Flat washers, M8, zinc plated',       qty: 950,  uom: 'EA',        unit: 0.12  },  // qty short
    { sku: 'AL-SH-3MM', desc: 'Aluminum sheet, 3mm, 1m x 2m',        qty: 20,   uom: 'EA',        unit: 85.00 },  // match
    // GR-SOLD missing
    { sku: 'COND-34',   desc: '3/4" EMT conduit',                    qty: 620,  uom: 'linear ft', unit: 3.20  },  // over qty; UOM normalises → LF
    { sku: 'FS-SURCHG', desc: 'Fuel surcharge (5%)',                 qty: 1,    uom: 'EA',        unit: 165.70, _new: true },
    { sku: 'EXP-HNDL',  desc: 'Expedited handling fee',              qty: 1,    uom: 'EA',        unit: 35.00,  _new: true },
  ],
  shipping: 50.00,
  taxRate: 0.08,
};

const SEED_VENDOR_RULES = {
  'Acme Industrial Supply': [
    {
      id: 'r1',
      kind: 'tolerance',
      pattern: 'BR-204-S',
      note: 'Steel L-brackets allowed ±3% unit-price drift (market index).',
      added: '2026-02-11',
    },
    {
      id: 'r2',
      kind: 'ignore',
      pattern: 'FS-SURCHG',
      note: 'Disregard 5% fuel surcharge variance — pre-approved per vendor agreement v3.',
      added: '2025-11-04',
    },
    {
      id: 'r3',
      kind: 'note',
      pattern: '*',
      note: 'Standard shipping rate updated to $50 effective Q2 2026.',
      added: '2026-03-30',
    },
  ],
};

// ── line-item totals ─────────────────────────────────────────────────────
const lineTotal = (it) => +(it.qty * it.unit).toFixed(2);
const subtotal  = (inv) => inv.items.reduce((s, it) => s + lineTotal(it), 0);
const tax       = (inv) => +(subtotal(inv) * inv.taxRate).toFixed(2);
const grandTotal = (inv) => +(subtotal(inv) + (inv.shipping || 0) + tax(inv)).toFixed(2);

// The authoritative billed amount for an invoice: the grand total EXACTLY as
// printed. It already nets tax, shipping, and any footer adjustments (restocking
// fees, handling, misc) that the merchandise subtotal omits — so it's the single
// "voice of truth" for spend, even when subtotal + tax + shipping disagrees.
// Signed (negative for a credit). Falls back to a derived total for invoices
// parsed before `total` was captured (those need a re-analyze for true accuracy);
// the fallback prefers the stated footer subtotal/tax (incl. negatives) so a
// credit's sign survives, only recomputing from line items when none is stated.
const statedInvoiceTotal = (inv) => {
  if (inv && inv.total != null && Math.abs(inv.total) > 0) return +(+inv.total).toFixed(2);
  const sub = inv.subtotal ? +inv.subtotal : subtotal(inv);
  const tx  = inv.subtotal && inv.taxTotal != null ? +inv.taxTotal : tax(inv);
  return +(sub + tx + (inv.shipping || 0)).toFixed(2);
};

// The stated tax on an invoice (signed) — the footer figure when present, else
// derived. Used to strip tax on tax-exempt jobs.
const statedInvoiceTax = (inv) =>
  (inv && inv.subtotal && inv.taxTotal != null) ? +(+inv.taxTotal).toFixed(2) : tax(inv);

// Spend attributed to an invoice in the budget rollups. The stated total is the
// billed truth — it nets adjustments (restock/handling fees) the subtotal omits.
//   - Taxed job: stated total, for everything.
//   - Tax-exempt + CREDIT: still the stated total. A credit memo's total is the
//     real net refund (incl. any fees/tax it nets); trust it as-is.
//   - Tax-exempt + REGULAR invoice: drop the tax that doesn't apply to an exempt
//     job — pre-tax basis = total − tax = subtotal + shipping + any NON-tax fees.
const invoiceSpend = (inv, taxExempt) =>
  (taxExempt && !inv.isCredit)
    ? +(statedInvoiceTotal(inv) - statedInvoiceTax(inv)).toFixed(2)
    : statedInvoiceTotal(inv);

// The invoiced average unit price is derived (summed line totals ÷ quantity), so
// comparing it to the quoted unit with === spuriously flags sub-cent rounding
// noise — a $10.3205 contract unit re-derives as $10.32 and looks "changed".
// Treat unit prices that agree within half a cent as identical.
const UNIT_PRICE_EPSILON = 0.005;
const pricesDiffer = (a, b) => a != null && b != null && Math.abs(a - b) > UNIT_PRICE_EPSILON;

// ── line-item matcher (single source of truth) ────────────────────────────
// Both the budget aggregator (aggregateInvoices) and the variance engine
// (computeVariances) pair quote lines to invoice lines through matchLineItems,
// so the two screens can never disagree about what matched what.

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = [];
  for (let i = 0; i <= m; i++) {
    dp[i] = [i];
    for (let j = 1; j <= n; j++) dp[i][j] = i === 0 ? j : 0;
  }
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i-1] === b[j-1]
        ? dp[i-1][j-1]
        : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
    }
  }
  return dp[m][n];
}

function fracToNum(s) {
  s = (s || '').trim();
  if (s.includes('-')) {
    const dash = s.lastIndexOf('-');
    const whole = parseFloat(s.slice(0, dash)) || 0;
    const parts = s.slice(dash + 1).split('/');
    return whole + (parts.length === 2 ? parseInt(parts[0], 10) / parseInt(parts[1], 10) : 0);
  }
  if (s.includes('/')) {
    const parts = s.split('/');
    return parseInt(parts[0], 10) / parseInt(parts[1], 10);
  }
  return parseFloat(s) || 0;
}

// Canonicalize a pipe/fitting description so abbreviation differences,
// packaging suffixes, and dimension-order variations don't block a match.
// Imperial trade sizes are halves through thirty-seconds, so a power-of-two
// denominator marks a real size; a packaging ratio is a round count ("5/50").
const TRADE_DENOM_RE = /^(?:2|4|8|16|32)$/;

// `keepTrailingSize` is opted into by the COMPARISON layer only (matchNormalize).
// itemKeyOf hashes the DEFAULT output as the identity for learned alias rules
// stored in /vendorRules, so the default must stay byte-stable — changing it
// would silently stop a user's manual links from resolving.
function normalizeForMatch(desc, { keepTrailingSize = false } = {}) {
  let s = (desc || '').toLowerCase();
  s = s.replace(/^ln\s+/, '');
  // Mixed fractions: "1 1/8" (space) is the same size as "1-1/8" (hyphen).
  // Canonicalize to the hyphen form so the two spellings match everywhere.
  s = s.replace(/(\d)\s+(\d+\/\d+)/g, '$1-$2');
  s = s.replace(/([\d\/\-.]+)\s*(?:"|in\b|inch(?:es)?\b)/g, '$1');
  s = s.replace(/([\d\/\-.]+)\s*(?:'|ft\b|foot\b|feet\b)/g, '$1');
  s = s.replace(/([\d\/\-.]+)\s+x\s+([\d\/\-.])/g, '$1x$2');
  // FTG (street/fitting end) is a DIFFERENT connection than C (sweat cup) —
  // normalize its spacing but never conflate it with C, or a FTGxFIP adapter
  // silently matches a CxFIP adapter at a different price.
  s = s.replace(/\bftg\s*x\s*/g,          'ftgx');
  s = s.replace(/c\s*x\s*c\s*x\s*c/g,    'cxc');
  s = s.replace(/c\s*x\s*c/g,             'cxc');
  s = s.replace(/\bswt\b/g,               'c');
  s = s.replace(/\bsweat\b/g,             'c');
  s = s.replace(/\btubing\b/g,            'tube');
  s = s.replace(/\s*\(\s*\d+\s*\/\s*\d+\s*\)/g, '');
  s = s.replace(/\s+\d+\s*ln\/bdl\b.*/i,         '');
  s = s.replace(/\s+\d+\s*\/\s*(bx|box|bdl|bundle|bag|ctn|carton|ea|each)\b.*/i, '');
  // A bare trailing fraction is usually a packaging count ("...CxCxC TEE 5/50"),
  // but it can equally be a genuine trailing SIZE ("copper coupling stop 3/4",
  // "flat washers 3/8"). Stripping both emptied the dim signature for real
  // sizes, so whether a size took part in matching depended on WHERE it sat in
  // the description — a leading size survived, a trailing one did not (E-2).
  s = keepTrailingSize
    ? s.replace(/\s+(\d+)\/(\d+)\s*$/, (m, _num, den) => (TRADE_DENOM_RE.test(den) ? m : ''))
    : s.replace(/\s+\d+\/\d+\s*$/,     '');
  s = s.replace(/^([\d\-\/]+x)+[\d\-\/]+/, (match) =>
    match.split('x').sort((a, b) => fracToNum(b) - fracToNum(a)).join('x')
  );
  return s.replace(/\s+/g, ' ').trim();
}

// ── synonym dictionary (Stage 4) ────────────────────────────────────────────
// Token-level synonyms applied ON TOP of normalizeForMatch, so vendor
// abbreviations and noise words collapse before fuzzy / token / exact-normalized
// comparison. Map a token to '' to DROP it (pure noise like a stray "deg").
// Entries are lowercase and assume normalizeForMatch already ran (inch/ft marks
// gone). This deliberately does NOT feed itemKeyOf, so learned alias keys (which
// hash the plain normalized desc) stay stable across this change. Extend freely.
const MATCH_SYNONYMS = {
  // noise words / inline units the comparison shouldn't hinge on
  deg: '', degree: '', degrees: '', dia: '', diameter: '', nominal: '', nom: '',
  // material / finish abbreviations (targets are NOT guard words, so safe)
  alum: 'aluminum', aluminium: 'aluminum',
  galv: 'galvanized', galvanised: 'galvanized',
  // fitting-kind abbreviations → the word the quote usually spells out. The
  // product-word guard re-canonicalizes elbow→ell etc., so guards are unaffected
  // while plain text/token comparison now lines up ("90 ell" ↔ "90 elbow").
  elb: 'elbow', ell: 'elbow', el: 'elbow',
  cplg: 'coupling', cplng: 'coupling', coupl: 'coupling',
  red: 'reducer', adpt: 'adapter', adptr: 'adapter', nip: 'nipple',
  // count words → ea
  pc: 'ea', pcs: 'ea', pce: 'ea', piece: 'ea', pieces: 'ea', each: 'ea',
  // product-form words the estimator treats as the same base material
  spiral: '', straight: '',
};

// Full match-time normalization: canonical form + degree symbol + synonyms.
// Used by matchLineItems' comparison passes (NOT by itemKeyOf).
function matchNormalize(desc) {
  const base = normalizeForMatch(desc, { keepTrailingSize: true }).replace(/°/g, ' ');
  return base
    .split(/\s+/)
    .flatMap((t) => {
      const v = t in MATCH_SYNONYMS ? MATCH_SYNONYMS[t] : t;
      return v ? [v] : []; // drop tokens the dictionary maps to ''
    })
    .join(' ')
    .trim();
}

// ── identity & dimension guards ───────────────────────────────────────────
// A SKU is only a real identity when the vendor printed one. The parser emits
// "" when the document shows no part number; older data may carry LINE-N
// placeholders or generated GEN- slugs — none of those may exact-match.
const isAutoSku = (sku) => !sku || /^(LINE-\d+|GEN-)/i.test(sku);

// Stable identity for alias rules: real SKU, else normalized description.
// Survives re-parses and item reordering (positional ids do not).
const itemKeyOf = (it) =>
  !isAutoSku(it.sku) ? `sku:${it.sku}` : `desc:${normalizeForMatch(it.desc)}`;

// "3/4 EMT conduit" and "1/2 EMT conduit" are two edits apart yet different
// products. Before any fuzzy/token match, extract every size-like token
// (fractions, decimals, x-joined dimension groups, plus bare single letters —
// copper tube types L/M/K and the like) and require one side's set to be a
// subset of the other; conflicting sizes block the match outright.
function dimSignature(normDesc) {
  const nums = [];
  const letters = new Set();
  for (const tok of (normDesc || '').split(/\s+/)) {
    if (!tok) continue;
    if (/^[a-z]$/.test(tok)) { letters.add(tok); continue; }
    if (!/^[\d\/.\-x]+$/.test(tok) || !/\d/.test(tok)) continue; // skip model nos. etc.
    for (const part of tok.split('x')) {
      const v = fracToNum(part);
      if (v > 0) nums.push(+v.toFixed(4));
    }
  }
  nums.sort((a, b) => a - b);
  return { nums, letters };
}

function dimsCompatible(a, b) {
  // every value of the smaller multiset must appear in the larger
  const subNums = (s, t) => {
    const pool = [...t];
    return s.every((v) => {
      const i = pool.indexOf(v);
      if (i === -1) return false;
      pool.splice(i, 1);
      return true;
    });
  };
  const numsOk = a.nums.length <= b.nums.length ? subNums(a.nums, b.nums) : subNums(b.nums, a.nums);
  if (!numsOk) return false;
  const subLet = (s, t) => [...s].every((x) => t.has(x));
  return a.letters.size <= b.letters.size ? subLet(a.letters, b.letters) : subLet(b.letters, a.letters);
}

// ── partial size-variant helpers ────────────────────────────────────────────
// Estimates size-drift in the field: a quoted 1-1/2" union often comes back as
// a 2" union on the invoice. The dim guard rightly blocks a FULL match (they
// are different products), but they belong to the same estimate line — pass 6
// pairs such leftovers as a review-tier 'partial' match instead of reporting
// an unrelated missing/new-charge split.
const isDimToken  = (tok) => /^[\d\/.\-x]+$/.test(tok) && /\d/.test(tok);
const dimTokensOf = (normDesc) => (normDesc || '').split(/\s+/).filter(isDimToken);
const stripDims   = (normDesc) =>
  (normDesc || '').split(/\s+/).filter((t) => t && !isDimToken(t)).join(' ');

// Mean relative difference between two size vectors (aligned smallest-first);
// a dim present on only one side counts as a full unit. 0 = identical sizes.
function sizeGap(a, b) {
  const n = Math.max(a.nums.length, b.nums.length);
  if (!n) return 0;
  let s = 0;
  for (let k = 0; k < n; k++) {
    const x = a.nums[k], y = b.nums[k];
    s += (x == null || y == null) ? 1 : Math.abs(x - y) / Math.max(x, y);
  }
  return s / n;
}

// ── spec-token guard ────────────────────────────────────────────────────────
// Mixed letter+digit tokens carry the product spec: "sch80", "a53", "14ga",
// "as210eh". dimSignature skips them (they're not sizes), so without this a
// 12GA AS200EH strut channel and a 14GA AS210EH one read as the same item —
// the words agree, the dims agree, and the price gap (1.4x) clears the ratio
// guard. Group such tokens by their letter skeleton (digits → '#'); when both
// descriptions carry the same skeleton, the actual tokens must overlap.
// One-sided spec tokens never block (vendors abbreviate freely).
function specSignature(normDesc) {
  const specs = new Map(); // letter skeleton ("sch#", "#ga") → Set of full tokens
  for (const tok of (normDesc || '').split(/\s+/)) {
    if (!/[a-z]/.test(tok) || !/\d/.test(tok)) continue;
    const skel = tok.replace(/\d+/g, '#');
    if (!specs.has(skel)) specs.set(skel, new Set());
    specs.get(skel).add(tok);
  }
  return specs;
}

function specsCompatible(a, b) {
  for (const [skel, av] of a) {
    const bv = b.get(skel);
    if (!bv) continue;
    let overlap = false;
    for (const t of av) if (bv.has(t)) { overlap = true; break; }
    if (!overlap) return false;
  }
  return true;
}

// ── product-word guard ──────────────────────────────────────────────────────
// Vocabulary of words that define WHAT a line is. Words within a class are
// mutually exclusive: "1 BLK 150# CAP" is one dropped token away from
// "1 BLK 150# UNION IMPORT", and "3/4 BM 150# 90 ELL" (black malleable) reads
// 75%-similar to "3/4 SWEAT 90 ELL" (copper) — but CAP≠UNION and BM≠sweat are
// different products at any string distance. Subset semantics like the dim
// guard: a side that omits a class entirely never blocks ("TYPE L TUBE"
// still matches "TYPE L COPPER TUBE").
const PRODUCT_WORD_CLASSES = [
  // fitting / item kind
  ['ell', 'tee', 'cap', 'union', 'coupling', 'reducer', 'bushing', 'plug',
   'nipple', 'flange', 'cross', 'adapter', 'hanger', 'clamp', 'valve',
   'trap', 'strainer', 'gasket'],
  // material / joinery — 'c' is sweat/swt after normalizeForMatch
  ['c', 'bm', 'galv', 'brz', 'copper', 'ss', 'pvc', 'cpvc', 'sw'],
  // pipe end finish
  ['pe', 't&c', 'tbe', 'toe', 'be'],
  // wall / weight class (sch40 vs sch80 is covered by the spec guard)
  ['std', 'xh', 'xhvy', 'xxh', 'xs'],
  // pipe manufacture
  ['cw', 'erw', 'smls'],
];
const PRODUCT_WORD_SYNONYMS = {
  elbow: 'ell', elb: 'ell', cplg: 'coupling', red: 'reducer',
  reducing: 'reducer', adpt: 'adapter', brass: 'brz',
  galvanized: 'galv', galvanised: 'galv',
};
const PRODUCT_WORD_CLASS_OF = new Map();
PRODUCT_WORD_CLASSES.forEach((cls, ci) => cls.forEach((w) => PRODUCT_WORD_CLASS_OF.set(w, ci)));

function productWordsSignature(normDesc) {
  const out = PRODUCT_WORD_CLASSES.map(() => new Set());
  for (const raw of (normDesc || '').split(/\s+/)) {
    let tok = PRODUCT_WORD_SYNONYMS[raw] || raw;
    let ci = PRODUCT_WORD_CLASS_OF.get(tok);
    if (ci === undefined && tok.length > 3 && tok.endsWith('s')) {
      tok = tok.slice(0, -1); // plural → singular ("nipples" → "nipple")
      ci = PRODUCT_WORD_CLASS_OF.get(tok);
    }
    if (ci !== undefined) out[ci].add(tok);
  }
  return out;
}

function productWordsCompatible(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (!a[i].size || !b[i].size) continue;
    const [s, t] = a[i].size <= b[i].size ? [a[i], b[i]] : [b[i], a[i]];
    if (![...s].every((x) => t.has(x))) return false;
  }
  return true;
}

// ── angle guard ──────────────────────────────────────────────────────────────
// An elbow's angle is a hard identity attribute: a 4" 90° ELL and a 4" 45° ELL
// are different parts no matter how similar the text. We read it as a dedicated
// attribute (not just another size token) so it can hard-block fuzzy/partial
// pairings even when sizes agree. Only read when the line is plausibly a
// directional fitting, so a "90" in "90 FT" or a "45" gallon drum isn't misread.
const FITTING_ANGLES = new Set(['90', '45', '22', '11', '60', '30', '22.5', '11.25', '11.5']);
const FITTING_WORDS_RE = /\b(ell|elbow|elb|street|ftg)\b/;
function extractAngle(normDesc) {
  const s = normDesc || '';
  if (!FITTING_WORDS_RE.test(s)) return null;
  for (const tok of s.split(/\s+/)) {
    const m = tok.match(/^(\d{1,3}(?:\.\d+)?)(?:deg|d|°)?$/);
    if (m && FITTING_ANGLES.has(m[1])) return m[1];
  }
  return null;
}

// A fuzzy/token match is rejected when unit prices (after UOM conversion)
// differ by more than this ratio. Same-item price drift is a few percent;
// a near-2x gap on a merely-similar description almost always means a
// different product. Exact SKU / exact-description matches are NOT price
// guarded — genuine price jumps on a confirmed item must surface as variances.
const FUZZY_PRICE_RATIO = 1.75;

// Pair quote lines to invoice lines and return an array of
// { quote: <quoteItem|null>, iis: [<invoiceItems>], matchType, matchScore }.
// Invoice items carry _id / _invoiceId / _invoiceDate / uomNorm.
// Passes run strongest→weakest; every pass groups ALL remaining invoice
// items that match a quote line (not just the single best) so the same item
// bought across multiple invoices rolls up into one drawdown row.
//
// options.aliases: vendor rules of kind 'alias' ({ quoteKey, invoiceKey }
// from itemKeyOf) — learned manual links, applied first and unconditionally.
function matchLineItems(quote, invoices, options = {}) {
  if (!quote || !invoices) return [];
  const aliases = options.aliases || [];

  // Aggregate (lump-sum) lines never pair against a quote line, and a SKU that's
  // non-unique within its own document can't anchor an exact-SKU match. Both are
  // computed per source document and carried on the mapped item; the raw fields
  // are left untouched. (Re-derived here so invoices stored before line typing
  // existed are handled too.)
  const allInvItems = invoices.flatMap((inv) => {
    const ambig = ambiguousSkuSet(inv.items);
    return inv.items.map((it, idx) => ({
      ...it,
      _id: `${inv.id}::${idx}`,
      _invoiceId: inv.id,
      _invoiceDate: inv.date,
      uomNorm: normalizeUOM(it.uom),
      _lineType: lineTypeOf(it),
      _skuAmbiguous: ambig.has((it.sku || '').trim()),
    }));
  }).filter((it) => it._lineType !== 'aggregate');
  const qAmbig = ambiguousSkuSet(quote.items);
  const qItems = quote.items.map((it, idx) => ({
    ...it, _id: `q::${idx}`, _qIndex: idx, uomNorm: normalizeUOM(it.uom),
    _lineType: lineTypeOf(it), _skuAmbiguous: qAmbig.has((it.sku || '').trim()),
  })).filter((it) => it._lineType !== 'aggregate');

  const qMatched = new Set();
  const invMatched = new Set();
  const pairs = [];
  // meta = { reason, confidence, low, ruleId } — matchReason/confidence drive the
  // debug output and the auto-match confidence bands; `low` tags the 60–69% tier.
  const claim = (qi, iis, matchType, matchScore, meta = {}) => {
    const pair = {
      quote: qi, iis, matchType, matchScore,
      ruleId:       meta.ruleId,
      matchReason:  meta.reason,
      confidence:   meta.confidence,
      lowConfidence: !!meta.low,
    };
    pairs.push(pair);
    qMatched.add(qi._id);
    iis.forEach((m) => invMatched.add(m._id));
    return pair;
  };

  const normCache = new Map();
  // Synonym-aware normalization for all comparison passes (Stage 2 + Stage 4).
  const norm = (desc) => {
    if (!normCache.has(desc)) normCache.set(desc, matchNormalize(desc));
    return normCache.get(desc);
  };
  const sigCache = new Map();
  const sig = (desc) => {
    const n = norm(desc);
    if (!sigCache.has(n)) sigCache.set(n, dimSignature(n));
    return sigCache.get(n);
  };
  // Fuzzy-only product-identity guards: spec tokens (sch80/14ga/as210eh) and
  // exclusive product words (cap vs union, sweat vs BM) must not conflict.
  const specCache = new Map(), wordsCache = new Map();
  const productOk = (qDesc, iDesc) => {
    const qN = norm(qDesc), iN = norm(iDesc);
    if (!specCache.has(qN))  specCache.set(qN,  specSignature(qN));
    if (!specCache.has(iN))  specCache.set(iN,  specSignature(iN));
    if (!wordsCache.has(qN)) wordsCache.set(qN, productWordsSignature(qN));
    if (!wordsCache.has(iN)) wordsCache.set(iN, productWordsSignature(iN));
    return specsCompatible(specCache.get(qN), specCache.get(iN))
      && productWordsCompatible(wordsCache.get(qN), wordsCache.get(iN));
  };
  // Fuzzy-only sanity check: similar words but a >1.75x unit-price gap
  // (compared on the quote's UOM basis) means a different product.
  const priceOk = (qi, ii) => {
    const { unit } = convertToQuoteBasis(qi, ii);
    if (!(qi.unit > 0) || !(unit > 0)) return true;
    const r = unit / qi.unit;
    return r <= FUZZY_PRICE_RATIO && r >= 1 / FUZZY_PRICE_RATIO;
  };
  // Hard attribute guards for the guessing passes (fuzzy/token/cost-code/partial):
  // a different elbow angle is never the same part, and fittings (ell/tee/union/
  // reducer/…) are never size-varied — a 4" and a 6" elbow must NOT auto-pair,
  // they fall through to the exceptions desk for a human to link if intended.
  const angleCache = new Map();
  const angleOf = (desc) => {
    const n = norm(desc);
    if (!angleCache.has(n)) angleCache.set(n, extractAngle(n));
    return angleCache.get(n);
  };
  const anglesOk = (qDesc, iDesc) => {
    const a = angleOf(qDesc), b = angleOf(iDesc);
    return !(a && b && a !== b);
  };
  const fitCache = new Map();
  const isFitting = (desc) => {
    const n = norm(desc);
    if (!fitCache.has(n)) {
      if (!wordsCache.has(n)) wordsCache.set(n, productWordsSignature(n));
      fitCache.set(n, wordsCache.get(n)[0].size > 0); // class 0 = fitting kind
    }
    return fitCache.get(n);
  };
  const uomCompatible = (qi, ii) => convertToQuoteBasis(qi, ii).compatible;
  // Stage 5 confidence bands. Optimize for automation, not human review:
  //   ≥0.85         → auto match
  //   0.70–0.84     → auto match only if the quantity units reconcile
  //   0.60–0.69     → auto match but tag low-confidence (estimator may spot-check)
  //   <0.60         → not a match (surfaces via candidate ranking instead)
  const band = (sim, qi, ii) => {
    if (sim >= 0.85) return { ok: true, low: false };
    if (sim >= 0.70) return { ok: uomCompatible(qi, ii), low: false };
    if (sim >= 0.60) return { ok: true, low: true };
    return { ok: false, low: false };
  };
  const pct = (sim) => Math.round(sim * 100);

  // Pass 1: alias rules — learned manual links, keyed by stable item identity.
  // Index quote items by their key once so each rule is an O(1) bucket lookup
  // instead of re-scanning all of qItems with .find() every iteration. Buckets
  // keep qItems order, so picking the first still-unmatched entry matches the
  // old find() semantics even as claim() marks items during the loop.
  const qByKey = new Map();
  for (const x of qItems) {
    const k = itemKeyOf(x);
    if (!qByKey.has(k)) qByKey.set(k, []);
    qByKey.get(k).push(x);
  }
  for (const rule of aliases) {
    if (!rule.quoteKey || !rule.invoiceKey) continue;
    const bucket = qByKey.get(rule.quoteKey);
    if (!bucket) continue;
    let qi = null;
    for (const x of bucket) {
      if (!qMatched.has(x._id)) { qi = x; break; } // first still-unmatched in qItems order
    }
    if (!qi) continue;
    const ms = allInvItems.filter((ii) => !invMatched.has(ii._id) && itemKeyOf(ii) === rule.invoiceKey);
    if (ms.length) claim(qi, ms, 'alias', undefined, { reason: 'learned_alias', confidence: 100, ruleId: rule.id });
  }

  // Pass 2: exact SKU — only vendor-printed SKUs count (never empty/LINE-N/
  // GEN- placeholders), and the dimensions in the two descriptions must not
  // conflict. The dim guard protects multi-vendor jobs, where short numeric
  // SKUs from different catalogs can collide on unrelated products.
  for (const qi of qItems) {
    if (qMatched.has(qi._id) || isAutoSku(qi.sku) || qi._skuAmbiguous) continue;
    const ms = allInvItems.filter((ii) =>
      !invMatched.has(ii._id) && !isAutoSku(ii.sku) && !ii._skuAmbiguous && ii.sku === qi.sku &&
      dimsCompatible(sig(qi.desc), sig(ii.desc)));
    if (ms.length) claim(qi, ms, 'exact_sku', undefined, { reason: 'exact', confidence: 100 });
  }

  // Pass 3: high-confidence textual equivalence, in order of strength —
  //   3a exact    raw lowercase strings identical                  → 'exact'    100
  //   3b normalized canonical forms identical (abbrev/packaging)    → 'normalized' 99
  //   3c synonym  identical only after the synonym dictionary       → 'synonym'  96
  //   3d token-sort same word multiset, any order ("90 ell 10")     → 'normalized' 95
  const sortedTokens = (n) => n.split(' ').filter(Boolean).sort().join(' ');
  for (const qi of qItems) {
    if (qMatched.has(qi._id)) continue;
    const rawL = (qi.desc || '').toLowerCase().trim();
    let ms = allInvItems.filter((ii) => !invMatched.has(ii._id) && (ii.desc || '').toLowerCase().trim() === rawL);
    if (ms.length) { claim(qi, ms, 'exact_desc', undefined, { reason: 'exact', confidence: 100 }); continue; }

    const qN = norm(qi.desc);
    ms = allInvItems.filter((ii) => !invMatched.has(ii._id) && norm(ii.desc) === qN);
    if (ms.length) {
      // If the plain (pre-synonym) canonical forms already agree it's pure
      // normalization; otherwise the synonym dictionary is what bridged them.
      const plainEq = normalizeForMatch(qi.desc) === normalizeForMatch(ms[0].desc);
      claim(qi, ms, 'fuzzy_desc', 0, { reason: plainEq ? 'normalized' : 'synonym', confidence: plainEq ? 99 : 96 });
      continue;
    }

    const qSorted = sortedTokens(qN);
    if (!qSorted) continue;
    ms = allInvItems.filter((ii) => !invMatched.has(ii._id) && sortedTokens(norm(ii.desc)) === qSorted);
    if (ms.length) claim(qi, ms, 'fuzzy_desc', 0, { reason: 'normalized', confidence: 95 });
  }

  // Pass 4: fuzzy Levenshtein on normalized descriptions (≤18% of longer
  // string), guarded by dimension and price compatibility. Candidate edges are
  // claimed globally best-first (Kruskal-style) so each invoice line goes to
  // its CLOSEST quote line — not to whichever quote line iterates first.
  {
    const cands = [];
    for (const qi of qItems) {
      if (qMatched.has(qi._id)) continue;
      const qN = norm(qi.desc), qS = sig(qi.desc);
      for (const ii of allInvItems) {
        if (invMatched.has(ii._id)) continue;
        const iN     = norm(ii.desc);
        const maxLen = Math.max(qN.length, iN.length);
        if (!maxLen) continue;
        // Similarity = 1 − editDistance/maxLen. Auto-match floor is 0.60 (was an
        // 0.82-equivalent 0.18 edit ratio) to minimize false negatives. Edit
        // distance is at least the length gap, so skip the O(n·m) computation
        // when the lengths alone already drop similarity below the 0.60 floor.
        const limit = Math.floor(maxLen * 0.40);
        if (Math.abs(qN.length - iN.length) > limit) continue;
        if (!dimsCompatible(qS, sig(ii.desc))) continue;
        if (!anglesOk(qi.desc, ii.desc)) continue;
        if (!productOk(qi.desc, ii.desc)) continue;
        const d = levenshtein(qN, iN);
        if (!priceOk(qi, ii)) continue;
        const sim = 1 - d / maxLen;
        const b = band(sim, qi, ii);
        if (!b.ok) continue;
        cands.push({ qi, ii, rel: d / maxLen, d, sim, low: b.low });
      }
    }
    cands.sort((a, b) => a.rel - b.rel);
    const open = new Map(); // quote _id → pair created in this pass
    for (const c of cands) {
      const qId = c.qi._id;
      if (invMatched.has(c.ii._id)) continue;
      let pair = open.get(qId);
      if (!pair) {
        if (qMatched.has(qId)) continue;
        pair = claim(c.qi, [], 'fuzzy_desc', c.d, { reason: 'fuzzy', confidence: pct(c.sim), low: c.low });
        open.set(qId, pair);
      }
      pair.iis.push(c.ii);
      invMatched.add(c.ii._id);
    }
  }

  // Pass 4b: token-subset — ≥75% of the shorter description's tokens appear in
  // the longer one, with dimension-prefix awareness (bare "1-1/4" ⊂ "1-1/4x3/4").
  // Same dim/price guards and best-first claiming as pass 4.
  const tokenHits = (aTokens, bTokens, bSet) =>
    aTokens.filter((t) => bSet.has(t) || bTokens.some((bt) => bt.startsWith(t + 'x') || t.startsWith(bt + 'x'))).length;
  {
    const tokCache = new Map();
    const toks = (desc) => {
      const n = norm(desc);
      if (!tokCache.has(n)) tokCache.set(n, n.split(/\s+/).filter(Boolean));
      return tokCache.get(n);
    };
    const cands = [];
    for (const qi of qItems) {
      if (qMatched.has(qi._id)) continue;
      const qTokens = toks(qi.desc);
      if (qTokens.length < 2) continue;
      const qSet = new Set(qTokens), qS = sig(qi.desc);
      for (const ii of allInvItems) {
        if (invMatched.has(ii._id)) continue;
        const iTokens = toks(ii.desc);
        if (iTokens.length < 2) continue;
        if (!dimsCompatible(qS, sig(ii.desc))) continue;
        if (!anglesOk(qi.desc, ii.desc)) continue;
        if (!productOk(qi.desc, ii.desc)) continue;
        const iSet  = new Set(iTokens);
        const score = Math.max(
          tokenHits(qTokens, iTokens, iSet) / qTokens.length,
          tokenHits(iTokens, qTokens, qSet) / iTokens.length
        );
        if (!priceOk(qi, ii)) continue;
        const b = band(score, qi, ii); // token-overlap floor now 0.60 (was 0.75)
        if (b.ok) cands.push({ qi, ii, score, low: b.low });
      }
    }
    cands.sort((a, b) => b.score - a.score);
    const open = new Map();
    for (const c of cands) {
      const qId = c.qi._id;
      if (invMatched.has(c.ii._id)) continue;
      let pair = open.get(qId);
      if (!pair) {
        if (qMatched.has(qId)) continue;
        pair = claim(c.qi, [], 'fuzzy_desc', +(1 - c.score).toFixed(2), { reason: 'fuzzy', confidence: pct(c.score), low: c.low });
        open.set(qId, pair);
      }
      pair.iis.push(c.ii);
      invMatched.add(c.ii._id);
    }
  }

  // Pass 5: cost_code match — codes cover whole categories, so require
  // dimension compatibility to avoid lumping unrelated items together.
  for (const qi of qItems) {
    if (qMatched.has(qi._id) || !qi.cost_code) continue;
    const ms = allInvItems.filter((ii) =>
      !invMatched.has(ii._id) && ii.cost_code && ii.cost_code === qi.cost_code &&
      dimsCompatible(sig(qi.desc), sig(ii.desc)) && anglesOk(qi.desc, ii.desc) && productOk(qi.desc, ii.desc));
    if (ms.length) claim(qi, ms, 'cost_code', undefined, { reason: 'fuzzy', confidence: 65, low: true });
  }

  // Pass 6: partial size-variant — weakest pass, leftovers only. When the
  // descriptions are (near-)identical once size tokens are stripped and no
  // spec/product-word/angle conflict exists, the invoice line is the same
  // product at a different size ("1-1/2 BLK 150# UNION IMPORT" vs "2 BLK 150#
  // UNION IMPORT", or a quoted 4" 90 ell vs a field-bought 6" 90 ell). Estimates
  // approximate what the field needs, so pair them rather than splitting into
  // missing + new-charge rows. The hard identity guards still apply: a different
  // ANGLE (90 vs 45), product KIND (cap vs union), or SPEC (SCH40 vs SCH80) is a
  // different part and stays unmatched. NOT price guarded — a different size
  // legitimately carries a different price.
  {
    const stripCache = new Map();
    const stripped = (desc) => {
      const n = norm(desc);
      if (!stripCache.has(n)) stripCache.set(n, stripDims(n));
      return stripCache.get(n);
    };
    const cands = [];
    for (const qi of qItems) {
      if (qMatched.has(qi._id)) continue;
      const qS = sig(qi.desc);
      if (!qS.nums.length) continue; // no size token — nothing to size-vary
      const qStr = stripped(qi.desc);
      if (!qStr) continue;
      for (const ii of allInvItems) {
        if (invMatched.has(ii._id)) continue;
        const iS = sig(ii.desc);
        if (!iS.nums.length) continue;
        const iStr = stripped(ii.desc);
        if (!iStr) continue;
        const maxLen = Math.max(qStr.length, iStr.length);
        const limit  = Math.max(2, Math.floor(maxLen * 0.18));
        if (Math.abs(qStr.length - iStr.length) > limit) continue;
        if (!anglesOk(qi.desc, ii.desc)) continue;
        if (!productOk(qi.desc, ii.desc)) continue;
        const d = levenshtein(qStr, iStr);
        if (d > limit) continue;
        // The price-ratio guard is skipped in this pass because a different
        // SIZE legitimately carries a different price. That reasoning does not
        // hold when the sizes are IDENTICAL: such a pair is not a size variant
        // at all, it is the same item at a different price — precisely what the
        // fuzzy/token passes just rejected on price. Without this, a same-size
        // 2.6x jump fell through here and auto-paired as `partial` at
        // confidence 100 (the exact overcharge this tool exists to surface),
        // and whether it did so depended on where the size token sat in the
        // description. Re-apply the guard for the zero-gap case only.
        const gap = sizeGap(qS, iS);
        if (gap === 0 && !priceOk(qi, ii)) continue;
        cands.push({ qi, ii, rel: d / maxLen, gap });
      }
    }
    // Closest text first, then closest size — a quoted 1-1/2" union prefers
    // the 2" union over the 6" one when both are unclaimed.
    cands.sort((a, b) => (a.rel - b.rel) || (a.gap - b.gap));
    const open = new Map();    // quote _id → pair created in this pass
    const openSig = new Map(); // quote _id → dim signature of the size it took
    for (const c of cands) {
      const qId = c.qi._id;
      const iiDesc = c.ii.desc;
      if (invMatched.has(c.ii._id)) continue;
      let pair = open.get(qId);
      if (!pair) {
        if (qMatched.has(qId)) continue;
        const conf = Math.max(50, Math.round((1 - c.gap) * 100));
        pair = claim(c.qi, [], 'partial', +c.gap.toFixed(2), { reason: 'fuzzy', confidence: conf, low: conf < 70 });
        pair.matchNote = `size ${dimTokensOf(norm(c.qi.desc)).join(' ') || '—'}`
          + ` vs ${dimTokensOf(norm(iiDesc)).join(' ') || '—'}`;
        open.set(qId, pair);
        openSig.set(qId, sig(iiDesc));
      } else if (!dimsCompatible(openSig.get(qId), sig(iiDesc))) {
        // one substitute size per quote line — don't lump a 2" AND a 6" union
        // onto the same 1-1/2" row; the runner-up stays a new charge.
        continue;
      }
      pair.iis.push(c.ii);
      invMatched.add(c.ii._id);
    }
  }

  // Unmatched quote lines → missing; unmatched invoice lines → new charges.
  for (const qi of qItems) {
    if (!qMatched.has(qi._id)) pairs.push({ quote: qi, iis: [], matchType: 'missing_from_invoice' });
  }
  for (const ii of allInvItems) {
    if (!invMatched.has(ii._id)) pairs.push({ quote: null, iis: [ii], matchType: 'new_charge' });
  }

  // Stage 6 — candidate ranking. For each leftover (a New Material line, or a
  // quote line that went un-invoiced), record the closest opposite-side lines so
  // the UI / learning flow can offer a one-click link WITHOUT interrupting the
  // estimator. Blends edit similarity with token overlap so reordered or partly
  // abbreviated descriptions still rank. Stored, never auto-applied.
  const simBlend = (aDesc, bDesc) => {
    const a = norm(aDesc), b = norm(bDesc);
    const maxLen = Math.max(a.length, b.length);
    if (!maxLen) return 0;
    const editSim = 1 - levenshtein(a, b) / maxLen;
    const aTok = new Set(a.split(' ').filter(Boolean));
    const bTok = b.split(' ').filter(Boolean);
    const tokSim = (aTok.size && bTok.length)
      ? bTok.filter((t) => aTok.has(t)).length / Math.max(aTok.size, bTok.length)
      : 0;
    return Math.max(editSim, tokSim);
  };
  const topCandidates = (desc, pool) =>
    pool
      .flatMap((cand) => {
        const c = simBlend(desc, cand.desc);
        return c >= 0.30 ? [{ sku: cand.sku || '', desc: cand.desc, c }] : [];
      })
      .sort((a, b) => b.c - a.c)
      .slice(0, 3)
      .map((x) => ({ sku: x.sku, desc: x.desc, confidence: pct(x.c) }));
  for (const pair of pairs) {
    if (pair.matchType === 'new_charge') {
      pair.candidates = topCandidates(pair.iis[0].desc, qItems);
    } else if (pair.matchType === 'missing_from_invoice') {
      pair.candidates = topCandidates(pair.quote.desc, allInvItems);
    }
  }

  return pairs;
}

// ── shared row math ───────────────────────────────────────────────────────
// Folds one pairing's invoice lines into quote-basis totals; used by both
// aggregateInvoices and computeVariances so the two screens always agree.
// When a UOM can't be reconciled, uomMismatch is set and the caller must show
// a unit-mismatch status instead of qty/price flags (the raw numbers are kept
// so dollar totals still roll up — qty×unit is invariant under conversion).
function summarizeMatch(quotedItem, invoicedItems) {
  const conv = invoicedItems.map((ii) => ({ ii, ...convertToQuoteBasis(quotedItem, ii) }));
  const uomMismatch = !!quotedItem && invoicedItems.length > 0 && conv.some((c) => !c.compatible);

  const breakdown = conv.map(({ ii, qty, unit, converted }) => ({
    invoiceId: ii._invoiceId,
    date:      ii._invoiceDate,
    qty, unit,
    lineTotal: +(qty * unit).toFixed(2),
    srcItem:   ii, // original invoice line — lets the UI trace back to the PDF
    ...(converted ? { srcQty: ii.qty, srcUom: ii.uomNorm ?? normalizeUOM(ii.uom) } : {}),
  }));

  const invoicedQty     = +conv.reduce((s, c) => s + c.qty, 0).toFixed(4);
  // Average from the raw (unrounded) qty×price products — dividing the
  // cents-rounded line-total sum loses sub-cent precision and would make a
  // genuinely unchanged unit price read as different.
  const invoicedRawSum  = conv.reduce((s, c) => s + c.qty * c.unit, 0);
  const invoicedLineSum = +invoicedRawSum.toFixed(2);
  const invoicedAvgUnit = invoicedQty > 0 ? +(invoicedRawSum / invoicedQty).toFixed(4) : null;

  // If units couldn't be reconciled, label the invoiced qty with ITS unit.
  const invoicedUOM = uomMismatch
    ? (invoicedItems[0].uomNorm ?? normalizeUOM(invoicedItems[0].uom))
    : normalizeUOM(quotedItem?.uom ?? invoicedItems[0]?.uom ?? null);

  // Footage — direct LF or derived from stick count × stick length.
  const quotedLF   = quotedItem ? itemFootage(quotedItem) : null;
  const invFootage = invoicedItems.map((ii) => itemFootage(ii, ii.uomNorm)).filter((f) => f != null);
  const invoicedLF = invFootage.length ? +invFootage.reduce((s, f) => s + f, 0).toFixed(2) : null;

  return { breakdown, invoicedQty, invoicedLineSum, invoicedAvgUnit, invoicedUOM, uomMismatch, quotedLF, invoicedLF };
}

// ── trade segments ───────────────────────────────────────────────────────
// A job's trades (Piping, Insulation, …). A job with multiple trades may carry
// a separate quote per trade and route each invoice to one of them.
function getJobTrades(job) {
  return Array.isArray(job?.trades) ? job.trades : [];
}

// Normalize a job into independent comparison SEGMENTS — each is one quote vs.
// its own invoices, the self-contained unit aggregateInvoices/computeVariances
// expect. A job with 0 or 1 trade collapses to exactly ONE segment holding
// today's `job.quote` + `job.invoices`, so every existing screen is byte-for-byte
// unchanged. A multi-trade job yields one segment per (optionally selected) trade:
//   • the first trade's quote is `job.quotesByTrade[trade]` or, as a fallback,
//     the legacy `job.quote` (so the primary quote is never stranded);
//   • each invoice belongs to `inv.trade`; untagged invoices fall to the primary
//     trade so nothing is ever orphaned out of the totals.
// `selectedTrades` (array | null) optionally restricts a multi-trade job to a
// subset for the combined/scoped view; null/empty means all trades.
function getSegments(job, selectedTrades = null) {
  const trades      = getJobTrades(job);
  const allInvoices = Array.isArray(job?.invoices) ? job.invoices : [];
  if (trades.length <= 1) {
    return [{ trade: trades[0] || null, quote: job?.quote ?? null, invoices: allInvoices }];
  }
  const primary = trades[0];
  const byTrade = job?.quotesByTrade || {};
  const pick = (selectedTrades && selectedTrades.length)
    ? trades.filter((t) => selectedTrades.includes(t))
    : trades;
  return pick.map((trade) => ({
    trade,
    quote: trade === primary ? (byTrade[trade] ?? job?.quote ?? null) : (byTrade[trade] ?? null),
    invoices: allInvoices.filter((inv) => (inv.trade || primary) === trade),
  }));
}

// ── aggregation engine ───────────────────────────────────────────────────
// Folds N invoices into per-item running totals vs. the quote baseline,
// pairing lines via the shared matchLineItems.
// Row status: 'match' | 'price' | 'qty' | 'over' | 'missing' | 'added' | 'multi'
function aggregateInvoices(quote, invoices, rules = [], options = {}) {
  // Tax-exempt jobs compare on a PRE-TAX basis: tax is dropped from the budget
  // totals and the synthetic tax row is omitted (line items are already pre-tax).
  const taxExempt = !!options.taxExempt;
  const ruleMatchesSku = (rule, sku) =>
    rule.pattern === '*' || rule.pattern === sku || (!!sku && sku.startsWith(rule.pattern));

  /**
   * What a comparison row is flagged for. `uom` is optional because only
   * matcher-produced rows set it — the synthetic shipping / tax rows below are
   * built without it, and readers treat a missing `uom` as false.
   * @typedef {{ price: boolean, qty: boolean, over: boolean, missing: boolean,
   *             added: boolean, uom?: boolean }} RowFlags
   */
  /**
   * One line of the quote-vs-invoice comparison. Two producers share it: the
   * matcher rows (one per pair) and the synthetic shipping / tax / adjustment
   * rows appended after them, which carry `synthetic` and none of the
   * match-audit or footage-detail fields — hence the optionals.
   * @typedef {object} ComparisonRow
   * @property {'shipping' | 'tax' | 'adjustment'} [synthetic]
   * @property {string} sku
   * @property {string} desc
   * @property {object | null} [quoteItem] original quote line, for PDF trace-back
   * @property {string | null} uomNorm
   * @property {number | null} footage
   * @property {number | null} [quotedLF]
   * @property {number | null} [invoicedLF]
   * @property {number | null} quotedQty
   * @property {number | null} quotedUnit
   * @property {number} quotedTotal
   * @property {string | null} quotedUOM
   * @property {number | null} invoicedQty
   * @property {number} invoicedTotal signed — a credit is negative
   * @property {number | null} invoicedAvgUnit
   * @property {string | null} [invoicedUOM]
   * @property {number} delta invoicedTotal − quotedTotal, signed
   * @property {any[]} breakdown
   * @property {string} [matchType]
   * @property {number} [matchScore]
   * @property {string} [matchNote]
   * @property {string} [ruleId]
   * @property {string} [matchReason]
   * @property {number} [confidence]
   * @property {boolean} [lowConfidence]
   * @property {any[]} [candidates]
   * @property {RowFlags} flags
   * @property {string} status
   * @property {boolean} resolved
   * @property {any} resolvedBy the vendor rule that resolved it, if any
   */

  // Pair quote ↔ invoice lines via the shared matcher (alias rules learned
  // from manual links apply first), then sort so quote-backed rows render in
  // quote order and off-quote ("added") rows fall to the end.
  const pairs = matchLineItems(quote, invoices, {
    aliases: rules.filter((r) => r.kind === 'alias'),
  });
  pairs.sort((a, b) =>
    (a.quote ? a.quote._qIndex : Infinity) - (b.quote ? b.quote._qIndex : Infinity));

  /** @type {ComparisonRow[]} */
  const rows = pairs.map((pair) => {
    const quotedItem    = pair.quote;
    const invoicedItems = pair.iis;
    const sku           = quotedItem?.sku || invoicedItems[0]?.sku || '—';

    const rawUOM  = quotedItem?.uom || invoicedItems[0]?.uom || null;
    const uomNorm = normalizeUOM(rawUOM);

    const {
      breakdown, invoicedQty, invoicedLineSum, invoicedAvgUnit, invoicedUOM,
      uomMismatch, quotedLF, invoicedLF,
    } = summarizeMatch(quotedItem, invoicedItems);

    const quotedQty       = quotedItem?.qty ?? null;
    const quotedUnit      = quotedItem?.unit ?? null;
    const quotedUOM       = normalizeUOM(quotedItem?.uom || null);
    const quotedLineTotal = quotedItem ? +lineTotal(quotedItem).toFixed(2) : 0;
    const footage         = invoicedLF ?? (isFootageUOM(uomNorm) ? invoicedQty : null);

    /** @type {RowFlags} */
    const flags = { price: false, qty: false, over: false, missing: false, added: false, uom: false };
    if (!quotedItem && invoicedItems.length > 0) {
      flags.added = true;
    } else if (quotedItem && invoicedItems.length === 0) {
      flags.missing = true;
    } else if (uomMismatch) {
      // Quantities/units are on incomparable bases — qty/price flags would be
      // noise. Surface a single "unit mismatch" for a human to reconcile.
      flags.uom = true;
    } else if (quotedItem && invoicedItems.length > 0) {
      if (pricesDiffer(invoicedAvgUnit, quotedUnit)) flags.price = true;
      if (invoicedQty > quotedQty)         flags.over  = true;
      else if (invoicedQty < quotedQty)    flags.qty   = true;
    }

    let resolved = false, resolvedBy = null;
    const anyFlag = flags.added || flags.price || flags.qty || flags.over || flags.missing || flags.uom;
    // A rule applies by SKU pattern OR — for the per-item "accept" written from
    // the Issues exceptions page — by the stable item key (works for lines that
    // have no real SKU, where a pattern can't single one row out).
    const itemKey = quotedItem ? itemKeyOf(quotedItem) : (invoicedItems[0] ? itemKeyOf(invoicedItems[0]) : null);
    for (const r of rules) {
      const appliesBySku = ruleMatchesSku(r, sku);
      const appliesByKey = !!r.quoteKey && !!itemKey && r.quoteKey === itemKey;
      if (!appliesBySku && !appliesByKey) continue;
      if (r.kind === 'ignore' && anyFlag) { resolved = true; resolvedBy = r; break; }
      if (r.kind === 'tolerance' && appliesBySku && flags.price && !flags.qty && !flags.over && !flags.added && !flags.missing) {
        if (Math.abs((invoicedAvgUnit - quotedUnit) / quotedUnit) <= 0.03) {
          resolved = true; resolvedBy = r; break;
        }
      }
    }

    let status = 'match';
    if (flags.missing) status = 'missing';
    else if (flags.added) status = 'added';
    else if (flags.uom) status = 'uom';
    else {
      const active = ['price', 'qty', 'over'].filter((k) => flags[k]);
      if (active.length === 1) status = active[0];
      else if (active.length > 1) status = 'multi';
    }

    return {
      sku, desc: quotedItem?.desc || invoicedItems[0]?.desc,
      quoteItem: quotedItem || null, // original quote line — PDF trace-back
      uomNorm, footage, quotedLF, invoicedLF,
      quotedQty, quotedUnit, quotedTotal: quotedLineTotal, quotedUOM,
      invoicedQty, invoicedTotal: invoicedLineSum, invoicedAvgUnit, invoicedUOM,
      delta: +(invoicedLineSum - quotedLineTotal).toFixed(2),
      breakdown,
      matchType: pair.matchType, matchScore: pair.matchScore,
      matchNote: pair.matchNote, ruleId: pair.ruleId,
      matchReason: pair.matchReason, confidence: pair.confidence,
      lowConfidence: pair.lowConfidence, candidates: pair.candidates,
      flags, status, resolved, resolvedBy,
    };
  });

  // Prefer each invoice's STATED subtotal/tax (what the document actually
  // prints) over re-deriving from line items. Per-line qty×unit rounding and a
  // recomputed subtotal×taxRate otherwise drift the headline "Invoiced" a few
  // cents from the real invoices (the on-screen invoice card already shows the
  // stated figures, so this keeps the budget rollup in step with it).
  // Use the stated footer figures when present (most reliable) — including the
  // NEGATIVE values on a credit memo, which a `> 0` test would wrongly discard
  // and recompute from line items (where a dropped sign flips a credit positive).
  const invSubtotal = (inv) => (inv.subtotal ? +(+inv.subtotal).toFixed(2) : subtotal(inv));
  const invTax      = (inv) => (inv.subtotal && inv.taxTotal != null ? +(+inv.taxTotal).toFixed(2) : tax(inv));

  // Synthetic: shipping
  const quotedShip   = quote.shipping || 0;
  const invoicedShip = +invoices.reduce((s, inv) => s + (inv.shipping || 0), 0).toFixed(2);
  const shipFlag     = quotedShip !== invoicedShip;
  const shipRule     = rules.find((r) => r.kind === 'note' && /shipping/i.test(r.note)) || null;
  rows.push({
    synthetic: 'shipping', sku: '—', desc: 'Shipping',
    uomNorm: null, footage: null,
    quotedQty: null, quotedUnit: null, quotedTotal: quotedShip,   quotedUOM: null,
    invoicedQty: null, invoicedTotal: invoicedShip, invoicedAvgUnit: null,
    delta: +(invoicedShip - quotedShip).toFixed(2), breakdown: [],
    flags: { price: shipFlag, qty: false, over: false, missing: false, added: false },
    status: shipFlag ? 'price' : 'match',
    resolved: shipFlag && !!shipRule, resolvedBy: shipRule,
  });

  // Synthetic: tax. A tax-exempt job compares pre-tax — both the quote and the
  // invoices drop their tax (an invoice may still PRINT tax, but it doesn't apply
  // here, so it's excluded from the budget). On a taxed job, the stated tax nets
  // for every document.
  const quotedTax   = taxExempt ? 0 : tax(quote);
  const invoicedTax = taxExempt ? 0 : +invoices.reduce((s, inv) => s + invTax(inv), 0).toFixed(2);
  const taxFlag     = quotedTax !== invoicedTax;
  if (quotedTax !== 0 || invoicedTax !== 0) {
    rows.push({
      synthetic: 'tax', sku: '—', desc: `Tax (${(quote.taxRate * 100).toFixed(2)}%)`,
      uomNorm: null, footage: null,
      quotedQty: null, quotedUnit: null, quotedTotal: quotedTax,   quotedUOM: null,
      invoicedQty: null, invoicedTotal: invoicedTax, invoicedAvgUnit: null,
      delta: +(invoicedTax - quotedTax).toFixed(2), breakdown: [],
      flags: { price: taxFlag, qty: false, over: false, missing: false, added: false },
      status: taxFlag ? 'price' : 'match',
      resolved: false, resolvedBy: null,
    });
  }

  // Summary. The invoiced total is each document's spend basis summed (see
  // invoiceSpend): the stated total, except a REGULAR invoice on a tax-exempt job
  // drops its tax (credits keep their net total). The quote baseline is pre-tax on
  // a tax-exempt job too.
  const quotedSub     = subtotal(quote);
  const invoicedSub   = +invoices.reduce((s, inv) => s + invSubtotal(inv), 0).toFixed(2);
  const quotedTotal   = taxExempt ? +(quotedSub + quotedShip).toFixed(2) : grandTotal(quote);
  const invoicedGrand = +invoices.reduce((s, inv) => s + invoiceSpend(inv, taxExempt), 0).toFixed(2);

  // Synthetic: adjustments — whatever the spend basis carries beyond merchandise
  // + shipping + tax (a restocking fee, handling, misc credit). Surfaced so the
  // breakdown reconciles to the invoiced total instead of silently differing.
  const invoicedAdj = +(invoicedGrand - invoicedSub - invoicedShip - invoicedTax).toFixed(2);
  if (Math.abs(invoicedAdj) >= 0.005) {
    rows.push({
      synthetic: 'adjustment', sku: '—', desc: 'Adjustments (restock / fees)',
      uomNorm: null, footage: null,
      quotedQty: null, quotedUnit: null, quotedTotal: 0, quotedUOM: null,
      invoicedQty: null, invoicedTotal: invoicedAdj, invoicedAvgUnit: null,
      delta: invoicedAdj, breakdown: [],
      flags: { price: true, qty: false, over: false, missing: false, added: false },
      status: 'price',
      resolved: false, resolvedBy: null,
    });
  }
  const flagged       = rows.filter((r) => r.status !== 'match' && !r.resolved);
  const matched       = rows.filter((r) => r.status === 'match'  || r.resolved);
  const footageSum    = rows.reduce((s, r) => s + (r.footage || 0), 0);

  return {
    rows,
    summary: {
      quotedTotal, invoicedTotal: invoicedGrand, taxExempt,
      delta: +(invoicedGrand - quotedTotal).toFixed(2),
      quotedSub, invoicedSub,
      quotedShip, invoicedShip,
      quotedTax,  invoicedTax,
      invoiceCount:  invoices.length,
      matchPct:      rows.length ? matched.length / rows.length : 1,
      flaggedCount:  flagged.length,
      matchCount:    matched.length,
      rowCount:      rows.length,
      resolvedCount: rows.filter((r) => r.resolved).length,
      totalFootage:  footageSum > 0 ? footageSum : null,
    },
  };
}

// Backward-compat alias — single-invoice callers still work unchanged.
const compareInvoices = (a, b, rules) => aggregateInvoices(a, [b], rules);

// ── Unified material state — Good / Needs Review / Problem ─────────────────
// One plain-English status per material, folding the two technical axes the UI
// used to expose — match CONFIDENCE (how sure we paired the right line) and
// variance SEVERITY (how far the numbers drift) — into three buckets a PM can
// act on without learning the matcher. The detailed reasoning is returned in
// `reasons` (and callers still have row.matchType/score) for an expandable
// "details" disclosure; it is intentionally absent from `level`/`label`.
//
//   problem — over-pulled (negative remaining), a hard/critical dollar swing,
//             or a charge that isn't on the RFQ. Someone has to decide.
//   review  — a soft drift, OR the line matched on a loose/partial/fuzzy rule
//             and should be eyeballed, OR units couldn't be reconciled.
//   good    — clean match within tolerance (or already settled by a rule);
//             includes quoted-but-not-yet-ordered lines (nothing wrong yet).
const MATERIAL_STATE_META = {
  good:    { label: 'Good',         tone: 'ok'   },
  review:  { label: 'Needs Review', tone: 'warn' },
  problem: { label: 'Problem',      tone: 'bad'  },
};

function materialState(row, tolerances) {
  const tols = {
    ...(typeof DEFAULT_TOLERANCES !== 'undefined' ? DEFAULT_TOLERANCES : {}),
    ...(tolerances || {}),
  };
  const out = (level, reasons) => ({ level, ...MATERIAL_STATE_META[level], reasons });

  // A vendor rule already settled this drift — trust the human decision.
  if (row.resolved) {
    return out('good', [row.resolvedBy?.kind === 'ignore'
      ? 'Settled by a vendor rule'
      : 'Within an accepted vendor tolerance']);
  }
  // Billed for something not on the RFQ — there's nothing to draw down against.
  if (row.flags.added) return out('problem', ['Off-quote charge — not on the RFQ']);
  // Quoted but nothing invoiced yet — outstanding, not an error.
  if (row.flags.missing) return out('good', ['Not invoiced yet — still outstanding']);
  // Units can't be reconciled (e.g. EA vs LF) — needs a human to compare.
  if (row.flags.uom) return out('review', ['Unit mismatch — quantities are not directly comparable']);

  const sev  = (typeof classifyVariance === 'function') ? classifyVariance(row, tols) : 'WITHIN_TOLERANCE';
  const conf = (typeof describeMatch === 'function') ? (describeMatch(row.matchType, row.matchScore).tier) : 'high';
  const lowConf  = conf === 'review' || conf === 'none';     // fuzzy / token / partial / cost_code
  const overPull = row.quotedQty != null && row.invoicedQty != null && row.invoicedQty > row.quotedQty;
  const priceUp  = row.quotedUnit != null && row.invoicedAvgUnit != null
    && row.invoicedAvgUnit > row.quotedUnit + 0.005;

  const reasons = [];
  if (overPull) {
    const over = +(row.invoicedQty - row.quotedQty).toFixed(2);
    reasons.push(`Over-pulled by ${over.toLocaleString()} ${row.uomNorm || 'units'}`);
  }
  if (priceUp) {
    const p = row.quotedUnit > 0 ? Math.round(((row.invoicedAvgUnit - row.quotedUnit) / row.quotedUnit) * 100) : null;
    reasons.push(p != null ? `Unit price up ${p}%` : 'Unit price increased');
  }
  if (lowConf) reasons.push('Matched on a loose rule — verify it’s the same item');

  // Over-ordering is the headline negative the PM asked us to surface.
  let level = 'good';
  if (overPull) level = 'problem';
  else if (sev === 'CRITICAL' || sev === 'HARD_FLAG') level = 'problem';
  else if (sev === 'SOFT_FLAG' || lowConf) level = 'review';

  if (level === 'good') {
    // On a live job a line is routinely mid-drawdown. That is not a variance,
    // but it isn't a completed match either — say which, so a PM can tell an
    // exhausted line from one still being bought against.
    const short = row.quotedQty > 0 && row.invoicedQty != null && row.invoicedQty < row.quotedQty;
    reasons.push(short
      ? `Partially invoiced — ${(+row.invoicedQty).toLocaleString()} of ${(+row.quotedQty).toLocaleString()} ${row.uomNorm || 'units'} so far`
      : 'Matches the quote');
  }
  return out(level, reasons);
}

// ── Canonical classification — ONE source of truth for counts everywhere ───
// Dashboard/Jobs flag badge, Materials split, Summary tally, and the Issues
// page all run their rows through this so the numbers can never disagree.
// "open issues" = problem + review; quoted materials and off-quote charges are
// split so "every quoted material" never folds in new charges. Exposure sums
// |variance| over open rows but skips unit-mismatch lines (their dollar delta
// is computed across incomparable bases and would be meaningless).
function classifyRows(rows, tolerances) {
  const real       = (rows || []).filter((r) => !r.synthetic);
  const materials  = real.filter((r) => !r.flags.added);  // on the RFQ
  const newCharges = real.filter((r) =>  r.flags.added);  // off-quote
  const states = new Map();
  const count  = { good: 0, review: 0, problem: 0 };
  let exposure = 0;
  for (const r of real) {
    const st = materialState(r, tolerances);
    states.set(r, st);
    count[st.level]++;
    if (st.level !== 'good' && !r.flags.uom) exposure += Math.abs(r.delta || 0);
  }
  return {
    real, materials, newCharges, states, count,
    open: count.problem + count.review,
    exposure: +exposure.toFixed(2),
  };
}

// Console/debug access (the window assignments earlier in this file cover the
// storage layer + migration tools); the ES module surface below is what the
// rest of the app imports.
if (typeof window !== 'undefined') Object.assign(window, {
  fmtUSD, fmtDelta,
  normalizeUOM, isFootageUOM,
  stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, dimSignature, dimsCompatible,
  specSignature, specsCompatible, productWordsSignature, productWordsCompatible,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
  aggregateInvoices, compareInvoices, materialState, classifyRows,
  getJobTrades, getSegments,
  lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  levenshtein, fracToNum, normalizeForMatch, matchLineItems,
});

export {
  // formatting + shared utilities
  fmtUSD, fmtDelta, safeUrl, firestoreSafe,
  csvCell, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  // role system
  ROLES, ROLE_LABEL, ROLE_COLOR,
  // storage layer (M2)
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep,
  useVendorRules, __migrateJobInvoices, __migrateVendorRules,
  // UOM + matching engine
  normalizeUOM, isFootageUOM, displayUOM, stickFeet, itemFootage,
  convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, dimSignature, dimsCompatible,
  specSignature, specsCompatible, productWordsSignature, productWordsCompatible,
  levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  // aggregation + classification
  aggregateInvoices, compareInvoices, materialState, classifyRows,
  MATERIAL_STATE_META, getJobTrades, getSegments,
  lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  // sample/seed data
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
};
